import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Building2, Plus, Search, Users, UserPlus, User,
  Mail, FileText, Tag, Pencil,
  X, Trash2, RefreshCw, Merge, ChevronDown, ChevronUp, Check,
  AlertTriangle, SlidersHorizontal, CreditCard, CheckCircle, Snowflake,
  Sparkles, AlertCircle, Archive, Filter, Scale, Printer,
  Package, Download, Loader2, Wrench, FolderOpen, ShieldAlert,
  TrendingUp, ChevronRight, BarChart2,
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useAuth } from '../context/AuthContext';
import { SidePanel } from '../components/SidePanel';
import { formatPhone, formatPrice } from '../utils/format';
import { ActivitesFichePanel, useHasActivite, ActivityPulse } from '../components/ActivitesFichePanel';
import DocumentsTab from '../components/DocumentsTab';

import { PhoneInput } from '../components/PhoneInput';
import { PaysSelect } from '../components/PaysSelect';
// pageCache retiré — pagination côté serveur

// ─── Constants ────────────────────────────────────────────────────────────────
const TYPES_ENT = {
  client:               { label: 'Client',              color: '#22C55E', bg: '#F0FDF4' },
  fournisseur:          { label: 'Fournisseur',         color: '#7C3AED', bg: '#FAF5FF' },
  prospect:             { label: 'Prospect',            color: '#D97706', bg: '#FFFBEB' },
  organisme_financier:  { label: 'Organisme financier', color: '#1D4ED8', bg: '#EFF6FF' },
};
const TYPE_LIST = Object.entries(TYPES_ENT).map(([value, meta]) => ({ value, ...meta }));

// Groupes de tags pour le filtre Annuaire (même taxonomie que settings/ParametresGeneraux)
const TAG_FILTER_GROUPS = [
  { key: 'statut',        label: 'Statuts entreprise',    iconColor: '#0369A1', bg: '#EFF6FF' },
  { key: 'automation',    label: 'Automatisation e-mail', iconColor: '#0891B2', bg: '#ECFEFF' },
  { key: 'fonction',      label: 'Fonctions',              iconColor: '#7C3AED', bg: '#F5F3FF' },
  { key: 'qualification', label: 'Qualifications',         iconColor: '#15803D', bg: '#F0FDF4' },
  { key: null,            label: 'Personnalisés',           iconColor: '#6B7280', bg: '#F8FAFC' },
];

const EMPTY_ENT = { nom: '', types_entreprise: [], email: '', telephone: '', adresse: '', code_postal: '', ville: '', pays: 'France', pays_code: 'FR', siret: '', siren: '', tva_intra: '', site_web: '', notes: '', tags: [], assurance_propre: false, forme_juridique: '', code_naf: '', libelle_naf: '', date_creation_societe: '', capital: '', odoo_id: '', ibans: [] };

// ─── IBAN helpers ──────────────────────────────────────────────────────────────
const formatIban = (raw) => (raw || '').replace(/\s/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();
const normalizeIban = (input) => (input || '').replace(/\s/g, '').toUpperCase();
function validateIban(iban) {
  const norm = normalizeIban(iban);
  if (!norm) return 'IBAN requis';
  if (norm.length < 15 || norm.length > 34) return 'Longueur IBAN invalide';
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(norm)) return 'Format IBAN invalide';
  return null;
}

// ─── Formatters légaux ─────────────────────────────────────────────────────────
function fmtSiret(raw) {
  const d = (raw || '').replace(/\D/g, '').slice(0, 14);
  if (!d) return '';
  return [d.slice(0,3), d.slice(3,6), d.slice(6,9), d.slice(9,14)].filter(g => g).join(' ');
}
function fmtSiren(raw) {
  const d = (raw || '').replace(/\D/g, '').slice(0, 9);
  if (!d) return '';
  return [d.slice(0,3), d.slice(3,6), d.slice(6,9)].filter(g => g).join(' ');
}
function fmtCapitalDisplay(raw) {
  if (raw === '' || raw == null) return '';
  const n = parseFloat(String(raw).replace(/\s/g, '').replace(',', '.'));
  if (isNaN(n)) return '';
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
const EMPTY_PER = { nom: '', prenom: '', fonctions: [], email: '', telephone_mobile: '', telephone_fixe: '', entreprise_id: '', entreprise_ids: [], notes: '', adresse: '', code_postal: '', ville: '', pays_code: 'FR' };

// ─── Badge type ───────────────────────────────────────────────────────────────
function TypeBadge({ type }) {
  const t = TYPES_ENT[type];
  if (!t) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: t.bg, color: t.color }}>
      {t.label}
    </span>
  );
}

// ─── Ghost row : recherche + liaison de contact ───────────────────────────────
function GhostContactSearch({ entrepriseId, linkedPers, onLinked }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.searchPersonnes(q, 10);
        const linkedIds = new Set(linkedPers.map(p => p.personne_id));
        setResults((r.data || []).filter(p => !linkedIds.has(p.personne_id)));
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, linkedPers]);

  const handleSelect = async (personne) => {
    try {
      await api.addPersonneEntreprise(personne.personne_id, entrepriseId);
      onLinked();
      setQ('');
      setResults([]);
      setOpen(false);
    } catch { toast.error('Erreur liaison contact'); }
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed transition-all"
           style={{ borderColor: 'var(--border)', background: 'var(--bg-secondary)' }}>
        <Search size={13} style={{ color: 'var(--text-placeholder)' }} />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Ajouter un contact…"
          className="flex-1 text-sm outline-none bg-transparent"
          style={{ color: 'var(--text)' }}
        />
        {loading && <span className="text-xs animate-pulse" style={{ color: 'var(--text-placeholder)' }}>…</span>}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 rounded-xl overflow-hidden shadow-xl"
             style={{ background: 'white', border: '1px solid var(--border)' }}>
          {results.map(p => {
            const fonctionTags = (p.personne_tags || []).filter(t => t.tag_type === 'fonction');
            const fonctions = fonctionTags.length > 0
              ? fonctionTags.map(t => t.nom)
              : (p.fonctions || (p.fonction ? [p.fonction] : []));
            return (
              <button key={p.personne_id}
                      className="w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-all flex items-center gap-2"
                      onMouseDown={() => handleSelect(p)}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                     style={{ background: 'var(--primary-50)', color: 'var(--primary-light)' }}>
                  {((p.prenom || p.nom || '?')[0]).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>
                    {[p.prenom, p.nom].filter(Boolean).join(' ')}
                  </p>
                  {p.entreprise_nom && (
                    <p className="text-xs truncate" style={{ color: 'var(--text-placeholder)' }}>
                      chez {p.entreprise_nom}
                    </p>
                  )}
                </div>
                {fonctions.slice(0, 1).map(f => (
                  <span key={f} className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>{f}</span>
                ))}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Ghost row : recherche + liaison d'entreprise (pour fiche contact) ────────
function GhostEntrepriseSearch({ linkedEntIds, onAdd }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.getEntreprises({ search: q });
        const linkedIds = new Set(linkedEntIds);
        setResults((r.data || []).slice(0, 8).filter(e => !linkedIds.has(e.entreprise_id)));
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, linkedEntIds]);

  const handleSelect = async (ent) => {
    await onAdd(ent);
    setQ('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed transition-all"
           style={{ borderColor: 'var(--border)', background: 'var(--bg-secondary)' }}>
        <Search size={13} style={{ color: 'var(--text-placeholder)' }} />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Ajouter une entreprise…"
          className="flex-1 text-sm outline-none bg-transparent"
          style={{ color: 'var(--text)' }}
        />
        {loading && <span className="text-xs animate-pulse" style={{ color: 'var(--text-placeholder)' }}>…</span>}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 rounded-xl overflow-hidden shadow-xl"
             style={{ background: 'white', border: '1px solid var(--border)' }}>
          {results.map(e => (
            <button key={e.entreprise_id}
                    className="w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-all flex items-center gap-2"
                    onMouseDown={() => handleSelect(e)}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                   style={{ background: 'var(--primary-50)', color: 'var(--primary-light)' }}>
                {(e.nom[0] || '?').toUpperCase()}
              </div>
              <span className="text-sm font-medium flex-1 truncate" style={{ color: 'var(--text)' }}>{e.nom}</span>
              {(e.types_entreprise || []).slice(0, 1).map(t => <TypeBadge key={t} type={t} />)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Dropdown multi-select pour critères de doublons ─────────────────────────
function CriteriaDropdown({ columns, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const toggle = (key) => onChange(selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key]);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-all hover:opacity-90"
        style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'white' }}>
        <SlidersHorizontal size={14} />
        Critères {selected.length > 0 && <span className="px-1.5 py-0.5 rounded-full text-xs font-bold" style={{ background: '#7C3AED', color: 'white' }}>{selected.length}</span>}
        <ChevronDown size={13} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 rounded-xl shadow-xl overflow-hidden" style={{ background: 'white', border: '1px solid var(--border)', minWidth: 240 }}>
          <div className="px-3 py-2 border-b text-xs font-semibold" style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Colonnes pour détecter les doublons</div>
          {columns.map(col => (
            <label key={col.key} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 transition-all">
              <div className={`w-4 h-4 rounded flex items-center justify-center border ${selected.includes(col.key) ? 'border-transparent' : ''}`}
                style={{ background: selected.includes(col.key) ? '#7C3AED' : 'white', borderColor: selected.includes(col.key) ? '#7C3AED' : 'var(--border)' }}>
                {selected.includes(col.key) && <Check size={10} color="white" strokeWidth={3} />}
              </div>
              <input type="checkbox" className="sr-only" checked={selected.includes(col.key)} onChange={() => toggle(col.key)} />
              <span className="text-sm" style={{ color: 'var(--text)' }}>{col.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
// ─── Onglet Conditions commerciales ──────────────────────────────────────────

const TYPES_REMISE = [
  { key: 'materiel',      label: 'Matériel' },
  { key: 'piece_detachee',label: 'Pièce dét.' },
  { key: 'prestation',    label: 'Prestation' },
  { key: 'main_oeuvre',   label: 'M.O.' },
  { key: 'location',      label: 'Location' },
];

function ConditionsTab({ entreprise, agences, onSave, saveRef }) {
  const [condMapping,   setCondMapping]   = useState([]);
  const [subTab,        setSubTab]        = useState('client');
  // Client
  const [cpAg,          setCpAg]          = useState(entreprise.conditions_paiement_par_agence || {});
  const [delaiBlockage, setDelaiBlockage] = useState(entreprise.delai_blocage ?? '');
  const [devise,        setDevise]        = useState(entreprise.devise || 'EUR');
  // Fournisseur
  const [cpAchat,       setCpAchat]       = useState(entreprise.conditions_paiement_achat_par_agence || {});

  useEffect(() => {
    api.getConditionsPaiementReferentiel().then(r => setCondMapping(r.data || [])).catch(() => {});
  }, [entreprise]);

  useEffect(() => {
    setCpAg(entreprise.conditions_paiement_par_agence || {});
    setDelaiBlockage(entreprise.delai_blocage ?? '');
    setDevise(entreprise.devise || 'EUR');
    setCpAchat(entreprise.conditions_paiement_achat_par_agence || {});
  }, [entreprise]);

  const handleSave = async () => {
    try {
      await api.patchEntrepriseConditions(entreprise.entreprise_id, {
        conditions_paiement_par_agence:       cpAg,
        conditions_paiement_achat_par_agence: cpAchat,
        delai_blocage: delaiBlockage !== '' ? parseInt(delaiBlockage, 10) : 0,
        devise: devise || 'EUR',
      });
      toast.success('Conditions enregistrées');
      onSave?.();
    } catch(e) { toast.error(e?.response?.data?.detail || 'Erreur enregistrement'); }
  };

  useEffect(() => { if (saveRef) saveRef.current = handleSave; }); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers Client ──────────────────────────────────────────────────────────
  const setField = (agence_id, field, val) =>
    setCpAg(prev => ({ ...prev, [agence_id]: { ...(prev[agence_id] || {}), [field]: val } }));

  const handleCondAgChange = (agence_id, nom) => {
    const match = condMapping.find(c => c.nom === nom);
    // "en_compte" uniquement si condition explicite avec délai ; sinon comptant par défaut
    const modeReglement = match && match.type_echeance !== 'comptant' ? 'en_compte' : 'comptant';
    setCpAg(prev => ({
      ...prev,
      [agence_id]: { ...(prev[agence_id] || {}), nom_condition: nom || null, mode_reglement: nom ? modeReglement : 'comptant' },
    }));
  };

  const setRemise = (agence_id, type, val) =>
    setCpAg(prev => {
      const cur = prev[agence_id] || {};
      return { ...prev, [agence_id]: { ...cur, remises: { ...(cur.remises || {}), [type]: val } } };
    });

  // ── Helpers Fournisseur ─────────────────────────────────────────────────────
  const handleCondAchatChange = (agence_id, nom) => {
    const match = condMapping.find(c => c.nom === nom);
    const modeReglement = match ? (match.type_echeance === 'comptant' ? 'comptant' : 'en_compte') : null;
    setCpAchat(prev => ({
      ...prev,
      [agence_id]: { ...(prev[agence_id] || {}), nom_condition: nom || null, mode_reglement: nom ? modeReglement : null },
    }));
  };

  const ModeBadge = ({ mode, type = 'client' }) => {
    const effective = mode || 'en_compte';
    const s = effective === 'comptant'
      ? { bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0', label: 'Comptant' }
      : { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE', label: 'En compte' };
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
        style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
        {s.label}
      </span>
    );
  };

  const inputSt = { borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--surface)' };

  return (
    <div className="px-6 py-5 space-y-5">

      {/* ── Sous-onglets Client / Fournisseur ── */}
      <div className="flex gap-1 border-b" style={{ borderColor: 'var(--border)' }}>
        {[{ key: 'client', label: 'Client' }, { key: 'fournisseur', label: 'Fournisseur' }].map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className="px-4 py-2 text-xs font-semibold transition-colors"
            style={subTab === t.key
              ? { color: 'var(--primary-solid)', borderBottom: '2px solid var(--primary-solid)', marginBottom: -1 }
              : { color: 'var(--text-muted)' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Onglet Client ── */}
      {subTab === 'client' && (<>
        {/* Conditions par agence — tableau complet */}
        {agences.length > 0 ? (
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)', background: 'var(--surface-alt, #FAFAF8)' }}>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Conditions par agence</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Plafond de crédit et remises applicables par agence.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: 780 }}>
                <thead>
                  <tr style={{ background: 'var(--surface-alt, #FAFAF8)', borderBottom: '1px solid var(--border)' }}>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)', minWidth: 130 }}>Agence</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)', minWidth: 130 }}>Délai de paiement</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)', minWidth: 110 }}>Mode règlement</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold" style={{ color: 'var(--text-muted)', minWidth: 95 }}>Plafond (€)</th>
                    {TYPES_REMISE.map(t => (
                      <th key={t.key} className="px-3 py-2.5 text-right text-xs font-semibold" style={{ color: 'var(--text-muted)', minWidth: 85 }}>Rem. {t.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {agences.map(a => {
                    const current = cpAg[a.agence_id] || {};
                    const remises = current.remises || {};
                    return (
                      <tr key={a.agence_id} className="border-b last:border-0" style={{ borderColor: 'var(--border-light)' }}>
                        <td className="px-3 py-2.5">
                          <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{a.nom}</span>
                          {a.societe_nom && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{a.societe_nom}</div>}
                        </td>
                        <td className="px-3 py-2.5">
                          <select value={current.nom_condition ?? ''} onChange={e => handleCondAgChange(a.agence_id, e.target.value || null)}
                            data-testid={`cond-agence-${a.agence_id}`}
                            className="text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 w-full"
                            style={{ ...inputSt, minWidth: 130 }}>
                            <option value="">— Non défini —</option>
                            {condMapping.map(c => <option key={c.condition_id} value={c.nom}>{c.nom}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2.5"><ModeBadge mode={current.mode_reglement} /></td>
                        <td className="px-3 py-2.5">
                          <input type="number" min="0" step="100" value={current.plafond ?? ''} placeholder="0"
                            data-testid={`plafond-${a.agence_id}`}
                            onChange={e => setField(a.agence_id, 'plafond', e.target.value ? parseFloat(e.target.value) : null)}
                            className="text-xs border rounded px-2 py-1 text-right focus:outline-none focus:ring-1 w-full"
                            style={{ ...inputSt, maxWidth: 80 }} />
                        </td>
                        {TYPES_REMISE.map(t => (
                          <td key={t.key} className="px-3 py-2.5">
                            <div className="flex items-center justify-end gap-1">
                              <input type="number" min="0" max="100" step="0.5" value={remises[t.key] ?? ''} placeholder="0"
                                data-testid={`remise-${a.agence_id}-${t.key}`}
                                onChange={e => setRemise(a.agence_id, t.key, e.target.value ? parseFloat(e.target.value) : 0)}
                                className="text-xs border rounded px-2 py-1 text-right focus:outline-none focus:ring-1"
                                style={{ ...inputSt, width: 52 }} />
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>%</span>
                            </div>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border p-5 text-center" style={{ borderColor: 'var(--border)' }}>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucune agence associée.</p>
          </div>
        )}

        {/* Paramètres commerciaux */}
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)', background: 'var(--surface-alt, #FAFAF8)' }}>
            <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Paramètres commerciaux</p>
          </div>
          <div className="px-4 py-3 flex items-center gap-3 border-b" style={{ borderColor: 'var(--border)' }}>
            <p className="text-xs flex-1" style={{ color: 'var(--text-muted)' }}>
              Nombre de jours après la plus ancienne échéance impayée au-delà duquel le client est bloqué. Laisser à 0 pour désactiver.
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <input type="number" min="0" max="9999" step="1" value={delaiBlockage} placeholder="0"
                data-testid="delai-blocage-input" onChange={e => setDelaiBlockage(e.target.value)}
                className="text-sm border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 text-right"
                style={{ ...inputSt, width: 72 }} />
              <span className="text-sm" style={{ color: 'var(--text-muted)' }}>jours</span>
              {delaiBlockage && parseInt(delaiBlockage, 10) > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>Actif</span>
              )}
            </div>
          </div>
          <div className="px-4 py-3 flex items-center justify-between gap-3">
            <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Devise préférée</label>
            <select value={devise} onChange={e => setDevise(e.target.value)} data-testid="devise-preferee-select"
              className="text-sm border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1"
              style={inputSt}>
              <option value="EUR">EUR — Euro</option>
              <option value="USD">USD — Dollar américain</option>
            </select>
          </div>
        </div>
      </>)}

      {/* ── Onglet Fournisseur ── */}
      {subTab === 'fournisseur' && (
        agences.length > 0 ? (
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)', background: 'var(--surface-alt, #FAFAF8)' }}>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Délais accordés par agence</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                Délais de paiement et mode de règlement par agence.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: 480 }}>
                <thead>
                  <tr style={{ background: 'var(--surface-alt, #FAFAF8)', borderBottom: '1px solid var(--border)' }}>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)', minWidth: 140 }}>Agence</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)', minWidth: 160 }}>Délai accordé</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)', minWidth: 130 }}>Mode règlement</th>
                  </tr>
                </thead>
                <tbody>
                  {agences.map(a => {
                    const current = cpAchat[a.agence_id] || {};
                    return (
                      <tr key={a.agence_id} className="border-b last:border-0" style={{ borderColor: 'var(--border-light)' }}>
                        <td className="px-3 py-2.5">
                          <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{a.nom}</span>
                          {a.societe_nom && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{a.societe_nom}</div>}
                        </td>
                        <td className="px-3 py-2.5">
                          <select value={current.nom_condition ?? ''} onChange={e => handleCondAchatChange(a.agence_id, e.target.value || null)}
                            data-testid={`cond-achat-${a.agence_id}`}
                            className="text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 w-full"
                            style={{ ...inputSt, minWidth: 150 }}>
                            <option value="">— Non défini —</option>
                            {condMapping.map(c => <option key={c.condition_id} value={c.nom}>{c.nom}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2.5">
                          <ModeBadge mode={current.mode_reglement} type="fournisseur" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border p-5 text-center" style={{ borderColor: 'var(--border)' }}>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucune agence associée.</p>
          </div>
        )
      )}
    </div>
  );
}

// ─── Modal gestion IBANs ──────────────────────────────────────────────────────
function ModalGestionIbans({ ibans, onChange, onSave, onClose }) {
  const [ibanInput, setIbanInput] = useState('');
  const [banqueInput, setBanqueInput] = useState('');
  const [ibanError, setIbanError] = useState('');
  const [editIdx, setEditIdx] = useState(null); // null=ajout, number=édition

  const startEdit = (idx) => {
    const item = ibans[idx];
    setEditIdx(idx);
    setIbanInput(item.iban);
    setBanqueInput(item.nom_banque || '');
    setIbanError('');
  };

  const cancelEdit = () => { setEditIdx(null); setIbanInput(''); setBanqueInput(''); setIbanError(''); };

  const handleSave = () => {
    const err = validateIban(ibanInput);
    if (err) { setIbanError(err); return; }
    const norm = normalizeIban(ibanInput);
    const duplicate = ibans.some((item, i) => item.iban === norm && i !== editIdx);
    if (duplicate) { setIbanError('IBAN déjà présent'); return; }
    const entry = { iban: norm, ...(banqueInput.trim() ? { nom_banque: banqueInput.trim() } : {}) };
    if (editIdx !== null) {
      const arr = [...ibans]; arr[editIdx] = entry; onChange(arr);
    } else {
      onChange([...ibans, entry]);
    }
    cancelEdit();
  };

  const handleDelete = (idx) => { onChange(ibans.filter((_, i) => i !== idx)); if (editIdx === idx) cancelEdit(); };

  const moveUp   = (idx) => { if (idx === 0) return; const a = [...ibans]; [a[idx-1], a[idx]] = [a[idx], a[idx-1]]; onChange(a); };
  const moveDown = (idx) => { if (idx === ibans.length - 1) return; const a = [...ibans]; [a[idx], a[idx+1]] = [a[idx+1], a[idx]]; onChange(a); };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--surface, #fff)', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', width: '100%', maxWidth: 480, padding: '24px 24px 20px' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <CreditCard size={16} style={{ color: 'var(--primary-solid)' }} />
            <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Coordonnées bancaires</h2>
          </div>
          <button onClick={onClose} style={{ padding: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
        </div>

        {/* Liste */}
        {ibans.length > 0 && (
          <div className="space-y-1.5 mb-5">
            {ibans.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-2 rounded-lg"
                style={{ background: 'var(--bg-muted, #f9fafb)', border: '1px solid var(--border)' }}>
                <div className="flex flex-col gap-0.5">
                  <button type="button" onClick={() => moveUp(idx)} disabled={idx === 0}
                    style={{ padding: 1, border: 'none', background: 'transparent', cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? 0.25 : 1, color: 'var(--text-muted)' }}>
                    <ChevronUp size={12} />
                  </button>
                  <button type="button" onClick={() => moveDown(idx)} disabled={idx === ibans.length - 1}
                    style={{ padding: 1, border: 'none', background: 'transparent', cursor: idx === ibans.length - 1 ? 'default' : 'pointer', opacity: idx === ibans.length - 1 ? 0.25 : 1, color: 'var(--text-muted)' }}>
                    <ChevronDown size={12} />
                  </button>
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="font-mono" style={{ fontSize: 12, color: 'var(--text)', letterSpacing: '0.04em' }}>{formatIban(item.iban)}</span>
                  {item.nom_banque && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{item.nom_banque}</span>}
                </div>
                <button type="button" onClick={() => startEdit(idx)}
                  style={{ padding: 3, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', borderRadius: 4 }}>
                  <Pencil size={12} />
                </button>
                <button type="button" onClick={() => handleDelete(idx)}
                  style={{ padding: 3, border: 'none', background: 'transparent', cursor: 'pointer', color: '#DC2626', borderRadius: 4 }}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Formulaire ajout / édition */}
        <div className="space-y-2 pt-4" style={{ borderTop: ibans.length > 0 ? '1px solid var(--border-light)' : 'none' }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
            {editIdx !== null ? `Modifier l'IBAN` : 'Ajouter un IBAN'}
          </p>
          <input type="text" value={ibanInput}
            onChange={e => { setIbanInput(e.target.value.toUpperCase()); setIbanError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" maxLength={42}
            style={{ width: '100%', border: `1px solid ${ibanError ? '#FCA5A5' : 'var(--border)'}`, borderRadius: 7, padding: '7px 10px', fontSize: 12, fontFamily: 'monospace', background: 'var(--surface)', color: 'var(--text)', outline: 'none', letterSpacing: '0.04em' }} />
          {ibanError && <p style={{ fontSize: 11, color: '#DC2626' }}>{ibanError}</p>}
          <input type="text" value={banqueInput}
            onChange={e => setBanqueInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            placeholder="Nom de la banque (optionnel)" maxLength={60}
            style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 7, padding: '7px 10px', fontSize: 12, background: 'var(--surface)', color: 'var(--text)', outline: 'none' }} />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-4">
          {editIdx !== null && (
            <button onClick={cancelEdit}
              style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', background: 'white', cursor: 'pointer' }}>
              Annuler
            </button>
          )}
          <button onClick={handleSave}
            style={{ padding: '7px 20px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 600, color: 'white', background: 'var(--primary-solid, #1D4ED8)', cursor: 'pointer' }}>
            {editIdx !== null ? 'Modifier' : 'Ajouter'}
          </button>
        </div>
        {/* Séparateur + Sauvegarder */}
        <div style={{ borderTop: '1px solid var(--border-light)', marginTop: 16, paddingTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={async () => { await onSave(); onClose(); }}
            style={{ padding: '8px 22px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, color: 'white', background: '#059669', cursor: 'pointer' }}>
            Sauvegarder
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function AddEmailInput({ onAdd }) {
  const [val, setVal] = React.useState('');
  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
  return (
    <div className="flex gap-2">
      <input
        type="email"
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && isValid) { onAdd(val.trim()); setVal(''); } }}
        placeholder="Ajouter une adresse email..."
        className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none"
        style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
      <button
        disabled={!isValid}
        onClick={() => { if (isValid) { onAdd(val.trim()); setVal(''); } }}
        className="px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40"
        style={{ background: '#2563EB' }}>
        Ajouter
      </button>
    </div>
  );
}

export default function Annuaire() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'entreprises';
  const setTab = (t) => setSearchParams({ tab: t });
  const location = useLocation();

  // ── Données ──────────────────────────────────────────────────────────────
  const [entreprises, setEntreprises] = useState([]);
  const [personnes,   setPersonnes]   = useState([]);
  const [allCatalogTags, setAllCatalogTags] = useState([]);  // catalogue complet
  const [plDossiers,   setPlDossiers]   = useState([]);
  const [plMenuRole,   setPlMenuRole]   = useState(null);   // null | 'customer' | 'supplier' — dropdown « Lier à PL »
  const [plSyncing,    setPlSyncing]    = useState(false);
  const [plSyncingPer, setPlSyncingPer] = useState(false);
  const [ledgerRefreshing, setLedgerRefreshing] = useState(false);

  const refreshLedger = async (role) => {
    if (!entPanel?.item?.entreprise_id) return;
    setLedgerRefreshing(true);
    try {
      const r = await api.syncLedgerTiers(entPanel.item.entreprise_id, role === 'customer' ? 'client' : 'fournisseur');
      toast.success(`Sync terminée — ${r.data?.upserted ?? 0} écriture(s) mise(s) à jour`);
      // Recharger les écritures
      const type = role === 'customer' ? 'client' : 'fournisseur';
      const fresh = await api.getEntrepriseComptabilite(entPanel.item.entreprise_id, type);
      setEntCompta({ loading: false, data: fresh.data, error: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur sync Pennylane');
    } finally {
      setLedgerRefreshing(false);
    }
  };
  const plMenuRef    = useRef(null);
  const [plMenuPerOpen, setPlMenuPerOpen] = useState(false);
  const plMenuPerRef = useRef(null);
  const [printMenuOpen, setPrintMenuOpen] = useState(false);
  const printMenuRef = useRef(null);
  const [showReleveMail,      setShowReleveMail]      = useState(false);
  const [releveMail,          setReleveMail]          = useState({ sujet: '', corps: '', destinataires: [] });
  const [releveMailLoading,   setReleveMailLoading]   = useState(false);
  const [releveMailSending,   setReleveMailSending]   = useState(false);
  const releveEntrepriseRef = useRef(null); // stocke l'entreprise quand le panel est fermé pour la modal
  const [entAppliedTags, setEntAppliedTags] = useState([]); // tags de l'ent ouverte
  const [tagFiltersSelected, setTagFiltersSelected] = useState(new Set());
  const [showTagAddMenu, setShowTagAddMenu] = useState(false);
  const tagAddMenuRef = useRef(null);
  const [perAppliedTags, setPerAppliedTags] = useState([]); // tags de la personne ouverte
  const [perTiersTags, setPerTiersTags] = useState([]);     // tags client (tiers_tags) de la personne ouverte
  const [openPerTagSection, setOpenPerTagSection] = useState(null); // 'fonction' | 'automation' | 'qualification'
  const perTagMenuRef = useRef(null);
  const [loading,     setLoading]     = useState(false);
  const { user, menuRights } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin_org';
  const canArchive    = isSuperAdmin || (Array.isArray(menuRights) && menuRights.includes('annuaire_archive'));
  const [annuaireLocked, setAnnuaireLocked] = useState(false);
  useEffect(() => {
    api.getAnnuaireLock().then(r => setAnnuaireLocked(r.data.annuaire_locked)).catch(() => {});
  }, []);
  const [search,      setSearch]      = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter,  setTypeFilter]  = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const tagDropdownRef = useRef(null);

  // Pagination côté serveur
  const [pageEnt,      setPageEnt]      = useState(1);
  const [totalEnt,     setTotalEnt]     = useState(0);
  const [totalPagesEnt,setTotalPagesEnt]= useState(1);
  const [pagePer,      setPagePer]      = useState(1);
  const [totalPer,     setTotalPer]     = useState(0);
  const [totalPagesPer,setTotalPagesPer]= useState(1);
  const [pageSize]                      = useState(50);
  const [fetchingEnt,  setFetchingEnt]  = useState(false);
  const [fetchingPer,  setFetchingPer]  = useState(false);
  const [metadataReady, setMetadataReady] = useState(false);
  // ── Selection ─────────────────────────────────────────────────────────────
  const [selectedEnt, setSelectedEnt] = useState(new Set());
  const [selectedPer, setSelectedPer] = useState(new Set());

  // ── Panneau Edition ───────────────────────────────────────────────────────
  const [entPanel, setEntPanel] = useState(null); // {item:null|{...}} — fiche entreprise
  const [perPanel, setPerPanel] = useState(null); // {item:null|{...}, _isParticulier, _fromEnt} — fiche contact (peut se superposer à entPanel)
  const [entTab, setEntTab] = useState('infos'); // onglet actif du panneau entreprise
  const [entDocCount, setEntDocCount] = useState(0);
  const { hasActivity } = useHasActivite('entreprise', entPanel?.item?.entreprise_id);
  const [entCompta, setEntCompta] = useState({ loading: false, data: [], error: null });
  const [entComptaFourn, setEntComptaFourn] = useState({ loading: false, data: [], error: null });
  const [entContactsList, setEntContactsList] = useState([]); // contacts de l'entreprise ouverte
  const [entForm, setEntForm] = useState(EMPTY_ENT);
  const [capitalEditing, setCapitalEditing] = useState(false);
  const [showIbansModal, setShowIbansModal] = useState(false);
  const [perForm, setPerForm] = useState(EMPTY_PER);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [agences, setAgences] = useState([]);
  const condSaveRef = useRef(null); // référence vers handleSave de ConditionsTab

  // ── Onglet Matériels ─────────────────────────────────────────────────────
  const [entMateriels, setEntMateriels] = useState({ loading: false, data: [], error: null });
  const [entAppetences, setEntAppetences] = useState({ loading: false, data: [], error: null });
  const [appetencesExpanded, setAppetencesExpanded] = useState(new Set()); // categorie_nom dépliées
  const [rapportAnnee, setRapportAnnee] = useState(new Date().getFullYear());
  const [rapportLoading, setRapportLoading] = useState(false);
  const [rapportMode, setRapportMode] = useState('proprietaire'); // "proprietaire" | "exploitant"
  const anneesCourantes = Array.from(
    { length: new Date().getFullYear() - 2026 + 1 },
    (_, i) => 2026 + i,
  );

  // ── Pappers ──────────────────────────────────────────────────────────────
  const [pappersQuery, setPappersQuery]           = useState('');
  const [pappersLoading, setPappersLoading]       = useState(false);
  const [pappersDirigeants, setPappersDirigeants] = useState([]);
  const [pappersProc, setPappersProc]             = useState(null); // procédure collective

  // ── Fonctions contacts (depuis les paramètres généraux) ──────────────────

  // ── Pappers : recherche par SIREN (9 chiffres exacts, 1 crédit) ─────────
  const handlePappersInput = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 9);
    setPappersQuery(digits);
    if (digits.length === 9) handlePappersSelect({ siren: digits });
  };

  const handlePappersSelect = async (suggestion) => {
    setPappersQuery('');
    setPappersLoading(true);
    try {
      const r = await api.pappersEntreprise(suggestion.siren);
      const d = r.data;
      setEntForm(f => ({
        ...f,
        nom:                  d.nom          || f.nom,
        siret:                d.siret        || f.siret,
        siren:                d.siren        || f.siren,
        forme_juridique:      d.forme_juridique || f.forme_juridique,
        code_naf:             d.code_naf     || f.code_naf,
        libelle_naf:          d.libelle_code_naf || f.libelle_naf,
        date_creation_societe: d.date_creation_societe || f.date_creation_societe,
        capital:              d.capital != null ? String(d.capital) : f.capital,
        adresse:              d.adresse      || f.adresse,
        code_postal:          d.code_postal  || f.code_postal,
        ville:                d.ville        || f.ville,
        tva_intra:            d.tva_intra    || f.tva_intra,
        email:                d.email        || f.email,
        telephone:            d.telephone    || f.telephone,
        site_web:             d.site_web     || f.site_web,
      }));
      setPappersDirigeants(d.dirigeants || []);
      setPappersProc(d.procedure_collective || null);
      if (d.procedure_collective) {
        toast.warning(`Procédure collective détectée : ${d.procedure_collective.type}`);
      } else {
        toast.success(`Fiche enrichie depuis Pappers`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur Pappers';
      toast.error(msg);
    } finally { setPappersLoading(false); }
  };

  // ── Sync PL ─────────────────────────────────────────────────────────────
  const [syncingPL, setSyncingPL] = useState(false);

  useEffect(() => {
    const h = (e) => { if (plMenuRef.current && !plMenuRef.current.contains(e.target)) setPlMenuRole(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Bouton « Lier à PL » — bandeaux « Comptes Pennylane » ──────────────────
  // role='customer' → onglet Compta client ; role='supplier' → Compta fournisseur.
  // Fournisseurs : liaison UNIQUEMENT sur match parfait SIRET/SIREN (+ IBAN étrangers).
  const doPlLink = async (role, societe_id) => {
    const kind = role === 'customer' ? 'Client' : 'Fournisseur';
    setPlMenuRole(null);
    setPlSyncing(true);
    try {
      const res = await api.matchTiersPennylane(entPanel.item.entreprise_id, societe_id, role);
      const { customer, supplier, not_found, message, from_cache, debug } = res.data;
      if (not_found) {
        console.error('[Lier à PL] not_found — payload debug:', JSON.stringify(debug, null, 2));
        toast.warning(message, { duration: 20000 });
      } else {
        const linked = role === 'customer' ? customer : supplier;
        const msg = linked
          ? (linked.compte ? `${kind} lié (${linked.compte})` : `${kind} lié (compte PL non assigné)`)
          : 'Déjà lié à Pennylane';
        toast.success(msg + (from_cache ? ' (cache)' : ''));
        const fresh = await api.getEntreprise(entPanel.item.entreprise_id);
        setEntPanel(prev => prev ? { ...prev, item: fresh.data } : prev);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur Pennylane');
    } finally {
      setPlSyncing(false);
    }
  };

  const renderPlLinkButton = (role) => {
    if (!entPanel?.item || plDossiers.length === 0) return null;
    const kind = role === 'customer' ? 'Client' : 'Fournisseur';
    const doLink = (societe_id) => doPlLink(role, societe_id);
    const btnStyle = { color: '#0F2D4A', border: '1px solid #22C491', background: '#2BD9A5' };
    const refreshBtn = (
      <button type="button"
        disabled={ledgerRefreshing}
        onClick={() => refreshLedger(role)}
        className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold border hover:opacity-80 disabled:opacity-50 transition-all"
        style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'white' }}
        title="Actualiser les écritures depuis Pennylane (sync complète pour ce tiers)">
        {ledgerRefreshing
          ? <span className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--text-muted)', borderTopColor: 'transparent' }} />
          : <RefreshCw size={12} />}
        {ledgerRefreshing ? '…' : 'Actualiser'}
      </button>
    );
    if (plDossiers.length === 1) {
      return (
        <div className="flex items-center gap-2 ml-auto shrink-0">
          {refreshBtn}
          <button type="button" data-testid={`btn-lier-pl-${role}`}
          disabled={plSyncing}
          onClick={() => doLink(plDossiers[0].societe_id)}
          className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-50 shrink-0"
          style={btnStyle}
          title={`Lier ce tiers à Pennylane par SIRET (${kind.toLowerCase()})`}>
          {plSyncing
            ? <span className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#0F2D4A', borderTopColor: 'transparent' }} />
            : <Scale size={12} />}
          {plSyncing ? '…' : 'Lier à PL'}
        </button>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 ml-auto shrink-0">
        {refreshBtn}
        <div className="relative" ref={plMenuRole === role ? plMenuRef : null}>
        <button type="button" data-testid={`btn-lier-pl-${role}`}
          disabled={plSyncing}
          onClick={() => setPlMenuRole(v => v === role ? null : role)}
          className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-50"
          style={btnStyle}
          title={`Lier ce tiers à Pennylane par SIRET (${kind.toLowerCase()})`}>
          {plSyncing ? '…' : <><Scale size={12} /> Lier à PL <ChevronDown size={10} /></>}
        </button>
        {plMenuRole === role && (
          <div className="absolute top-full mt-1 left-0 z-50 rounded-lg shadow-lg border overflow-hidden"
               style={{ background: 'white', borderColor: 'var(--border)', minWidth: '200px' }}>
            {plDossiers.map(d => (
              <button key={d.societe_id} type="button"
                data-testid={`pl-dossier-${role}-${d.societe_id}`}
                className="w-full text-left px-4 py-2.5 hover:bg-purple-50 transition-colors border-b last:border-b-0 text-xs"
                style={{ borderColor: '#EDE9FE' }}
                onClick={() => doLink(d.societe_id)}>
                <span className="font-medium" style={{ color: '#5B21B6' }}>{d.nom}</span>
              </button>
            ))}
          </div>
        )}
        </div>
      </div>
    );
  };


  useEffect(() => {
    const h = (e) => { if (plMenuPerRef.current && !plMenuPerRef.current.contains(e.target)) setPlMenuPerOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    const h = (e) => { if (printMenuRef.current && !printMenuRef.current.contains(e.target)) setPrintMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Charger les dossiers PL une seule fois
  useEffect(() => {
    api.tcPlGetDossiers().then(r => setPlDossiers(r.data?.dossiers || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const h = (e) => { if (tagAddMenuRef.current && !tagAddMenuRef.current.contains(e.target)) setShowTagAddMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    const h = (e) => { if (perTagMenuRef.current && !perTagMenuRef.current.contains(e.target)) setOpenPerTagSection(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);



  // ── Détection doublons ────────────────────────────────────────────────────
  const [dupPanel, setDupPanel]           = useState(false);
  const [dupColumns, setDupColumns]       = useState([]);
  const [dupCriteria, setDupCriteria]     = useState([]);
  const [dupGroups, setDupGroups]         = useState([]);
  const [dupDetecting, setDupDetecting]   = useState(false);
  const [dupMasters, setDupMasters]       = useState({}); // {groupKey: entreprise_id}
  const [merging, setMerging]             = useState(false);

  // ── Chargement metadata (fait une seule fois) ────────────────────────────
  const load = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    try {
      const tR = await api.getAnnuaireTags();
      setAllCatalogTags(tR.data || []);
      setMetadataReady(true);
    } catch (err) { console.error('Annuaire — chargement metadata:', err); toast.error('Erreur chargement annuaire'); }
    finally { if (!opts.silent) setLoading(false); }
  }, []);

  // Charger les agences une seule fois (pour l'onglet conditions commerciales)
  useEffect(() => {
    api.getSocietes().then(r => {
      const all = (r.data || []).flatMap(s => (s.agences || []).map(a => ({ ...a, societe_nom: s.nom })));
      setAgences(all);
    }).catch(() => {});
  }, []);

  // ── Fetch entreprises (paginé) ────────────────────────────────────────────
  const fetchEntreprises = useCallback(async (pg) => {
    if (!metadataReady) return;
    const currentPage = pg ?? pageEnt;
    setFetchingEnt(true);
    try {
      const params = { page: currentPage, limit: pageSize };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (typeFilter)              params.type_entreprise = typeFilter;
      if (showArchived)            params.include_archive = true;
      
      // Filtrage par tags sélectionnés
      if (tagFiltersSelected.size > 0) {
        params.tags = [...tagFiltersSelected].join(',');
      }
      
      const res = await api.getEntreprises(params);
      const data = res.data;
      setEntreprises(Array.isArray(data.items) ? data.items : []);
      setTotalEnt(data.total || 0);
      setTotalPagesEnt(data.pages || 1);
    } catch (err) { console.error('Annuaire — chargement entreprises:', err); toast.error('Erreur chargement entreprises'); }
    finally { setFetchingEnt(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadataReady, pageEnt, pageSize, debouncedSearch, typeFilter, showArchived, tagFiltersSelected]);

  // ── Fetch personnes (paginé) ───────────────────────────────────────────────
  const fetchPersonnes = useCallback(async (pg) => {
    if (!metadataReady) return;
    const currentPage = pg ?? pagePer;
    setFetchingPer(true);
    try {
      const params = { page: currentPage, limit: pageSize };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (tagFiltersSelected.size > 0) params.tags = [...tagFiltersSelected].join(',');
      const res = await api.getPersonnes(params);
      const data = res.data;
      setPersonnes(Array.isArray(data.items) ? data.items : []);
      setTotalPer(data.total || 0);
      setTotalPagesPer(data.pages || 1);
    } catch (err) { console.error('Annuaire — chargement contacts:', err); toast.error('Erreur chargement contacts'); }
    finally { setFetchingPer(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadataReady, pagePer, pageSize, debouncedSearch, tagFiltersSelected]);

  // Debounce search
  const searchTimerRef = useRef(null);
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  // Reset pages quand les filtres changent
  useEffect(() => { setPageEnt(1); }, [debouncedSearch, typeFilter, tagFiltersSelected]);
  useEffect(() => { setPagePer(1); }, [debouncedSearch, tagFiltersSelected]);
  useEffect(() => { }, [debouncedSearch]); // placeholder pour reset page contacts
  // Reset tags au changement d'onglet (portée différente)
  useEffect(() => { setTagFiltersSelected(new Set()); setTagDropdownOpen(false); }, [activeTab]);

  // Fermeture du dropdown tags au clic extérieur
  useEffect(() => {
    const handleClick = (e) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target))
        setTagDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggleTagFilter = (tagId) => {
    setTagFiltersSelected(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tagId)) newSet.delete(tagId);
      else newSet.add(tagId);
      return newSet;
    });
  };

  // Fetch entreprises sur changement de paramètres
  useEffect(() => { fetchEntreprises(); }, [fetchEntreprises]);
  // Fetch personnes sur changement de paramètres
  useEffect(() => { fetchPersonnes(); }, [fetchPersonnes]);
  // Fetch particuliers sur changement de paramètres (uniquement si onglet actif)
  useEffect(() => { if (activeTab === 'personnes') fetchPersonnes(); }, [fetchPersonnes, activeTab]);

  // Charger colonnes doublons au montage
  useEffect(() => {
    api.getDuplicateColumns().then(r => setDupColumns(r.data || [])).catch(() => {});
  }, []);

  // Auto-ouvrir une fiche entreprise si open_ent est dans l'URL (ex: venant de l'Atelier)
  useEffect(() => {
    const openEntId = searchParams.get('open_ent');
    if (!openEntId) return;
    api.getEntreprise(openEntId)
      .then(r => { openEntPanel(r.data); })
      .catch(() => {});
    // Nettoyer le param de l'URL après ouverture
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.delete('open_ent'); return p; });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Avec la pagination serveur, entreprises/personnes sont déjà filtrées
  const filteredEnt = entreprises;
  const filteredPer = personnes;

  // ── Lookups O(1) pour éviter les filter() par ligne dans le rendu ──────────
  // Map entreprise_id → nombre de contacts liés
  const entContactCountMap = useMemo(() => {
    const m = new Map();
    personnes.forEach(p => {
      const ids = p.entreprise_ids?.length ? p.entreprise_ids : (p.entreprise_id ? [p.entreprise_id] : []);
      ids.forEach(id => m.set(id, (m.get(id) || 0) + 1));
    });
    return m;
  }, [personnes]);

  // Map entreprise_id → entreprise (pour l'onglet contacts)
  const entByIdMap = useMemo(() => {
    const m = new Map();
    entreprises.forEach(e => m.set(e.entreprise_id, e));
    return m;
  }, [entreprises]);
  const entScrollRef = useRef(null);
  const perScrollRef = useRef(null);
  const entVirtualizer = useVirtualizer({
    count: filteredEnt.length,
    getScrollElement: () => entScrollRef.current,
    estimateSize: () => 45,
    overscan: 10,
  });
  const perVirtualizer = useVirtualizer({
    count: filteredPer.length,
    getScrollElement: () => perScrollRef.current,
    estimateSize: () => 45,
    overscan: 10,
  });


  // ─── Panneau entreprise ──────────────────────────────────────────────────
  const loadEntContacts = useCallback(async (entrepriseId) => {
    if (!entrepriseId) { setEntContactsList([]); return; }
    try {
      const r = await api.getEntreprise(entrepriseId);
      setEntContactsList(r.data.personnes || []);
    } catch { setEntContactsList([]); }
  }, []);

  const loadEntMateriels = useCallback(async (entrepriseId) => {
    if (!entrepriseId) return;
    setEntMateriels({ loading: true, data: [], error: null });
    try {
      const r = await api.getEntrepriseMateriels(entrepriseId);
      setEntMateriels({ loading: false, data: r.data || [], error: null });
    } catch (e) {
      setEntMateriels({ loading: false, data: [], error: 'Erreur chargement matériels' });
    }
  }, []);

  const loadEntAppetences = useCallback(async (entrepriseId) => {
    if (!entrepriseId) return;
    setEntAppetences({ loading: true, data: [], error: null });
    setAppetencesExpanded(new Set());
    try {
      const r = await api.getAppetencesLocation(entrepriseId);
      setEntAppetences({ loading: false, data: r.data || [], error: null });
    } catch (e) {
      setEntAppetences({ loading: false, data: [], error: 'Erreur chargement appétences' });
    }
  }, []);

  const handleTelechargerRapport = async (entrepriseId) => {
    setRapportLoading(true);
    try {
      const r = await api.getEntrepriseRapportPdf(entrepriseId, rapportAnnee, rapportMode);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_technique_${rapportAnnee}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Erreur génération PDF';
      if (e.response?.data instanceof Blob) {
        try { const t = await e.response.data.text(); msg = JSON.parse(t).detail || msg; } catch {}
      }
      toast.error(msg);
    } finally {
      setRapportLoading(false);
    }
  };

  const openEntPanel = (item) => {
    setEntForm(item ? { ...EMPTY_ENT, ...item } : EMPTY_ENT);
    setCapitalEditing(false);
    setEntTab('infos');
    setEntDocCount(0);
    setPappersDirigeants([]);
    setPappersProc(null);
    setPappersQuery('');
    setEntCompta({ loading: false, data: [], error: null });
    setEntComptaFourn({ loading: false, data: [], error: null });
    setEntContactsList([]);
    setEntMateriels({ loading: false, data: [], error: null });
    setEntAppetences({ loading: false, data: [], error: null });
    setAppetencesExpanded(new Set());
    setEntAppliedTags(item?.tags_list || []);
    setShowTagAddMenu(false);
    setEntPanel({ item });
    if (item) {
      loadEntContacts(item.entreprise_id);
      // Charger pennylane_comptes depuis le détail (absent de la liste)
      if (!item.pennylane_comptes) {
        api.getEntreprise(item.entreprise_id)
          .then(r => {
            const pl = r.data?.pennylane_comptes;
            if (pl !== undefined) {
              setEntPanel(p => p ? { ...p, item: { ...p.item, pennylane_comptes: pl } } : p);
            }
          })
          .catch(() => {});
      }
    }
  };

  const refreshEntTags = async (entreprise_id) => {
    try {
      const r = await api.getEntrepriseTags(entreprise_id);
      const tags = r.data || [];
      setEntAppliedTags(tags);
      const gele       = tags.some(t => t.comportement === 'geler');
      const contentieux = tags.some(t => t.comportement === 'contentieux');
      setEntPanel(p => ({ ...p, item: { ...p.item, gele, contentieux, tags_list: tags } }));
    } catch {}
  };

  const refreshPerTags = async (personne_id) => {
    try {
      const r = await api.getPersonneTags(personne_id);
      setPerAppliedTags(r.data || []);
    } catch {}
  };

  // ── Tags client (tiers_tags — Party Model) sur la fiche personne ─────────
  // B2C (particulier sans entreprise) : tags du tiers de la personne, éditables.
  // B2B (contact rattaché à une entreprise) : rappel lecture seule des tags client
  // du tiers de l'entreprise rattachée.
  const refreshPerTiersTags = async (item) => {
    if (!item) { setPerTiersTags([]); return; }
    try {
      const entIds = item.entreprise_ids?.length ? item.entreprise_ids : (item.entreprise_id ? [item.entreprise_id] : []);
      if (entIds.length > 0) {
        const r = await api.getEntrepriseTags(entIds[0]);
        setPerTiersTags(r.data || []);
      } else if (item.tiers_id) {
        const r = await api.getTiersTags(item.tiers_id);
        setPerTiersTags(r.data || []);
      } else {
        setPerTiersTags([]);
      }
    } catch { setPerTiersTags([]); }
  };

  const handleApplyPerTiersTag = async (tagId) => {
    const tid = perPanel?.item?.tiers_id;
    if (!tid) return;
    try {
      await api.applyTagToTiers(tid, tagId);
      await refreshPerTiersTags(perPanel.item);
      const tag = allCatalogTags.find(t => t.tag_id === tagId);
      toast.success(`Tag client "${tag?.nom || tagId}" appliqué`);
      setOpenPerTagSection(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur application du tag');
    }
  };

  const handleRemovePerTiersTag = async (tagId) => {
    const tid = perPanel?.item?.tiers_id;
    if (!tid) return;
    try {
      await api.removeTagFromTiers(tid, tagId);
      await refreshPerTiersTags(perPanel.item);
      toast.success('Tag client retiré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression du tag');
    }
  };

  const handleApplyPerTag = async (tagId) => {
    if (!perPanel?.item?.personne_id) return;
    try {
      await api.applyTagToPersonne(perPanel.item.personne_id, tagId);
      await refreshPerTags(perPanel.item.personne_id);
      const tag = allCatalogTags.find(t => t.tag_id === tagId);
      toast.success(`Tag "${tag?.nom || tagId}" appliqué`);
      setOpenPerTagSection(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur application du tag');
    }
  };

  const handleRemovePerTag = async (tagId) => {
    if (!perPanel?.item?.personne_id) return;
    try {
      await api.removeTagFromPersonne(perPanel.item.personne_id, tagId);
      await refreshPerTags(perPanel.item.personne_id);
      toast.success('Tag retiré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression du tag');
    }
  };

  const refreshEntNom = async (entreprise_id) => {
    try {
      const r = await api.getEntreprise(entreprise_id);
      const newNom = r.data?.nom;
      if (newNom) setEntPanel(p => ({ ...p, item: { ...p.item, nom: newNom } }));
    } catch {}
  };

  const handleApplyTag = async (tagId) => {
    if (!entPanel?.item) return;
    try {
      await api.applyTagToEntreprise(entPanel.item.entreprise_id, tagId);
      await refreshEntTags(entPanel.item.entreprise_id);
      const tag = allCatalogTags.find(t => t.tag_id === tagId);
      if (tag?.comportement === 'contentieux') await refreshEntNom(entPanel.item.entreprise_id);
      toast.success(`Tag "${tag?.nom || tagId}" appliqué`);
      setShowTagAddMenu(false);
      fetchEntreprises(pageEnt);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur application du tag');
    }
  };

  const handleRemoveTag = async (tagId) => {
    if (!entPanel?.item) return;
    try {
      await api.removeTagFromEntreprise(entPanel.item.entreprise_id, tagId);
      await refreshEntTags(entPanel.item.entreprise_id);
      const tag = allCatalogTags.find(t => t.tag_id === tagId);
      if (tag?.comportement === 'contentieux') await refreshEntNom(entPanel.item.entreprise_id);
      fetchEntreprises(pageEnt);
      toast.success('Tag retiré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression du tag');
    }
  };

  const handleToggleArchive = async () => {
    if (!entPanel?.item) return;
    try {
      const r = await api.archiveEntreprise(entPanel.item.entreprise_id);
      const newArchive = r.data.archive;
      setEntPanel(p => ({ ...p, item: { ...p.item, archive: newArchive } }));
      fetchEntreprises(pageEnt);
      toast.success(newArchive ? 'Contact archivé — retiré de la liste' : 'Contact désarchivé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur archivage');
    }
  };

  const saveEnt = async () => {
    if (!entForm.nom.trim()) { toast.error('Nom obligatoire'); return; }

    // ── Validation SIREN/SIRET/TVA obligatoires pour les nouvelles entreprises ─
    if (!entPanel?.item) {
      if (!entForm.siren?.trim() || !entForm.siret?.trim() || !entForm.tva_intra?.trim()) {
        toast.error(
          'SIREN, SIRET et TVA intracommunautaire sont obligatoires. ' +
          'Utilisez l\'autocomplétion SIREN (Pappers) avant d\'enregistrer.',
          { duration: 8000 }
        );
        return;
      }
    }

    setSaving(true);
    // Convertir les champs numériques : chaîne vide → null pour éviter l'erreur Pydantic
    const sanitizeEnt = (f) => ({
      ...f,
      capital: f.capital === '' || f.capital === undefined ? null : Number(f.capital) || null,
      odoo_id: f.odoo_id === '' || f.odoo_id === undefined ? null : Number(f.odoo_id) || null,
      ibans:   Array.isArray(f.ibans) ? f.ibans : [],
    });

    try {
      let res;
      if (entPanel?.item) {
        res = await api.updateEntreprise(entPanel.item.entreprise_id, sanitizeEnt(entForm));
        toast.success('Entreprise mise à jour');
        // Mettre à jour immédiatement l'entrée dans la liste locale
        setEntreprises(prev => prev.map(e =>
          e.entreprise_id === entPanel.item.entreprise_id ? { ...e, ...sanitizeEnt(entForm), nom: entForm.nom } : e
        ));
      } else {
        res = await api.createEntreprise(sanitizeEnt({ ...entForm, types_entreprise: entForm.types_entreprise.length ? entForm.types_entreprise : ['prospect'] }));
        toast.success('Entreprise créée');
      }
      if (res?.data?.odoo_warning) {
        toast.warning(`Sync Odoo échouée : ${res.data.odoo_warning}`, { duration: 10000 });
      } else if (res?.data?.odoo_synced_id) {
        toast.success(`Odoo #${res.data.odoo_synced_id}`, { duration: 4000 });
      }
      if (res?.data?.pl_created?.length > 0) {
        toast.success(`PennyLane : ${res.data.pl_created.join(' | ')}`, { duration: 6000 });
      }
      if (res?.data?.pl_warnings?.length > 0) {
        toast.warning(`PennyLane (non bloquant) : ${res.data.pl_warnings.join(' | ')}`, { duration: 10000 });
      }
      setEntPanel(null); load(); fetchEntreprises(1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleUnlinkContact = async (personne) => {
    try {
      await api.removePersonneEntreprise(personne.personne_id, entPanel.item.entreprise_id);
      toast.success(`${[personne.prenom, personne.nom].filter(Boolean).join(' ')} délié`);
      loadEntContacts(entPanel.item.entreprise_id);
    } catch { toast.error('Erreur déliaison'); }
  };

  const loadEntCompta = async (entrepriseId) => {
    setEntCompta({ loading: true, data: [], error: null });
    try {
      const r = await api.getEntrepriseComptabilite(entrepriseId, 'client');
      setEntCompta({ loading: false, data: r.data || [], error: null });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur chargement comptabilité';
      setEntCompta({ loading: false, data: [], error: msg });
    }
  };

  const loadEntComptaFourn = async (entrepriseId) => {
    setEntComptaFourn({ loading: true, data: [], error: null });
    try {
      const r = await api.getEntrepriseComptabilite(entrepriseId, 'fournisseur');
      setEntComptaFourn({ loading: false, data: r.data || [], error: null });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur chargement comptabilité fournisseur';
      setEntComptaFourn({ loading: false, data: [], error: msg });
    }
  };

  const handleAddPerEnt = async (ent) => {
    // Toujours mettre à jour le cache local pour affichage immédiat
    setLinkedEntsMap(m => ({ ...m, [ent.entreprise_id]: ent }));
    if (perPanel?.item) {
      try {
        await api.addPersonneEntreprise(perPanel.item.personne_id, ent.entreprise_id);
        setPerForm(f => ({ ...f, entreprise_ids: [...new Set([...f.entreprise_ids, ent.entreprise_id])] }));
        fetchPersonnes();
      } catch { toast.error('Erreur liaison entreprise'); }
    } else {
      setPerForm(f => ({ ...f, entreprise_ids: [...new Set([...f.entreprise_ids, ent.entreprise_id])] }));
    }
  };

  const handleRemovePerEnt = async (ent_id) => {
    setLinkedEntsMap(m => { const next = { ...m }; delete next[ent_id]; return next; });
    if (perPanel?.item) {
      try {
        await api.removePersonneEntreprise(perPanel.item.personne_id, ent_id);
        setPerForm(f => ({ ...f, entreprise_ids: f.entreprise_ids.filter(id => id !== ent_id) }));
        fetchPersonnes();
      } catch { toast.error('Erreur déliaison entreprise'); }
    } else {
      setPerForm(f => ({ ...f, entreprise_ids: f.entreprise_ids.filter(id => id !== ent_id) }));
    }
  };

  // ─── Panneau contact ─────────────────────────────────────────────────────
  const openPerPanel = (item) => {
    const ent_ids = item
      ? (item.entreprise_ids?.length ? item.entreprise_ids : (item.entreprise_id ? [item.entreprise_id] : []))
      : [];
    // Pré-charger le cache des noms d'entreprises depuis le champ enrichi backend
    const entMap = {};
    if (item?.entreprises_liees?.length) {
      item.entreprises_liees.forEach(e => { if (e?.entreprise_id) entMap[e.entreprise_id] = e; });
    } else if (item?.entreprise_id && item?.entreprise_nom) {
      entMap[item.entreprise_id] = { entreprise_id: item.entreprise_id, nom: item.entreprise_nom, types_entreprise: [] };
    }
    setLinkedEntsMap(entMap);
    setPerForm(item ? {
      nom: item.nom || '',
      prenom: item.prenom || '',
      fonctions: item.fonctions || [],
      email: item.email || '',
      telephone_mobile: item.telephone_mobile || '',
      telephone_fixe: item.telephone_fixe || '',
      entreprise_id: item.entreprise_id || '',
      entreprise_ids: ent_ids,
      notes: item.notes || '',
      adresse: item.adresse || '',
      code_postal: item.code_postal || '',
      ville: item.ville || '',
    } : EMPTY_PER);
    setPerAppliedTags([]);
    setPerTiersTags([]);
    setOpenPerTagSection(null);
    setPerPanel({ item, _isParticulier: !item ? !item?.entreprise_id : !item.entreprise_id });
    if (item?.personne_id) {
      refreshPerTags(item.personne_id);
      refreshPerTiersTags(item);
    }
  };

  // ── Ouverture automatique depuis navigation (ex: clic client dans contrat) ──
  useEffect(() => {
    const { openClientId, openClientType } = location.state || {};
    if (!openClientId) return;
    window.history.replaceState({}, '');
    if (openClientType === 'personne') {
      setTab('personnes');
      api.getPersonne(openClientId)
        .then(r => { openPerPanel(r.data); })
        .catch(() => {});
    } else {
      setTab('entreprises');
      api.getEntreprise(openClientId)
        .then(r => { openEntPanel(r.data); })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePer = async () => {
    if (!perForm.nom.trim()) { toast.error('Nom obligatoire'); return; }
    if (perForm.email && (perForm.email.includes(';') || perForm.email.includes(','))) {
      toast.error('Le champ email ne peut contenir qu\'une seule adresse. Pour plusieurs contacts, créez des fiches séparées.');
      return;
    }
    if (dupCheck?.blocked) {
      const label = dupCheck.blocking_reason === 'email' ? 'e-mail' : 'téléphone portable';
      toast.error(`Impossible de créer : un contact avec le même ${label} existe déjà.`);
      return;
    }
    setSaving(true);
    try {
      const ent_ids = [...new Set(perForm.entreprise_ids || [])];
      const payload = {
        ...perForm,
        entreprise_ids: ent_ids,
        entreprise_id: ent_ids[0] || null,
        adresse: perForm.adresse || null,
        code_postal: perForm.code_postal || null,
        ville: perForm.ville || null,
      };
      let res;
      if (perPanel?.item) {
        res = await api.updatePersonne(perPanel.item.personne_id, payload);
        toast.success('Contact mis à jour');
      } else {
        res = await api.createPersonne(payload);
        toast.success('Contact créé');
      }
      if (res?.data?.odoo_warning) {
        toast.warning(`Sync Odoo échouée : ${res.data.odoo_warning}`, { duration: 10000 });
      } else if (res?.data?.odoo_synced_id) {
        toast.success(`Odoo #${res.data.odoo_synced_id}`, { duration: 4000 });
      }
      setPerPanel(null); fetchPersonnes(1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  // ── Vérification doublons en temps réel (création uniquement) ──────────────
  useEffect(() => {
    if (!perPanel || perPanel?.item) { setDupCheck(null); return; }
    const { email, telephone_mobile, prenom, nom } = perForm;
    const hasEmail = email?.trim().length > 0;
    const hasTel   = telephone_mobile?.trim().length > 3;
    const hasName  = prenom?.trim().length > 0 && nom?.trim().length > 0;
    if (!hasEmail && !hasTel && !hasName) { setDupCheck(null); return; }
    clearTimeout(dupCheckTimer.current);
    dupCheckTimer.current = setTimeout(async () => {
      try {
        const params = {};
        if (hasEmail) params.email = email.trim();
        if (hasTel)   params.telephone_mobile = telephone_mobile.trim();
        if (hasName)  { params.prenom = prenom.trim(); params.nom = nom.trim(); }
        const r = await api.checkDuplicatePersonne(params);
        setDupCheck(r.data);
      } catch { /* silencieux */ }
    }, 600);
    return () => clearTimeout(dupCheckTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perForm.email, perForm.telephone_mobile, perForm.prenom, perForm.nom, perPanel]);


  const deletePer = async (id) => {
    setDeleting(true);
    try {
      await api.deletePersonne(id);
      toast.success('Contact supprimé');
      setPerPanel(null); fetchPersonnes(1);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur suppression';
      toast.error(msg, { duration: 10000 });
    }
    finally { setDeleting(false); }
  };

  // ─── Sync IBANs Odoo ─────────────────────────────────────────────────────
  const handleSyncPLTiers = async () => {
    setSyncingPL(true);
    try {
      const r = await api.syncAllTiersPennylane();
      const { jobs } = r.data;
      toast.info(`Synchronisation Pennylane lancée pour ${jobs.length} société(s)…`);

      // Polling jusqu'à ce que tous les jobs soient terminés
      const pending = new Set(jobs.map(j => j.job_id));
      let synced = 0, ambiguous = 0, errors = 0;

      const poll = setInterval(async () => {
        try {
          const results = await Promise.all(
            [...pending].map(jid =>
              api.getSyncTiersStatus(jid)
                .then(r => ({ jid, ...r.data }))
                .catch(err => {
                  // 404 = job expiré (redémarrage serveur) → on traite comme terminé
                  const status = err?.response?.status;
                  if (status === 404) return { jid, status: 'completed', synced: 0, ambiguous: 0, errors: 0 };
                  throw err; // autre erreur réseau → remonte
                })
            )
          );
          let lastJob = null;
          for (const job of results) {
            if (job.status === 'completed' || job.status === 'done') {
              pending.delete(job.jid);
              synced    += job.synced    || 0;
              ambiguous += job.ambiguous || 0;
              errors    += job.errors    || 0;
              lastJob = job;
            } else if (job.status === 'error') {
              pending.delete(job.jid);
              errors++;
            }
          }
          if (pending.size === 0) {
            clearInterval(poll);
            setSyncingPL(false);
            const msg = `Sync Pennylane terminée — ${synced} liés, ${ambiguous} ambigus${errors ? `, ${errors} erreur(s)` : ''}`;
            if (synced === 0 && lastJob) {
              const plTotal = lastJob.pl_customers_total ?? '?';
              const bedeeTotal = lastJob.bedee_total ?? '?';
              const notFound = lastJob.not_found_in_bedee ?? 0;
              const diagDetail = `PL: ${plTotal} clients · Bedee: ${bedeeTotal} sociétés · Non trouvés: ${notFound}`;
              const samplePL = (lastJob.diag_pl_noms || []).slice(0,3).join(', ');
              const sampleBedee = (lastJob.diag_bedee_noms || []).slice(0,3).join(', ');
              toast.warning(`${msg}\n${diagDetail}${samplePL ? `\nPL: ${samplePL}` : ''}${sampleBedee ? `\nBedee: ${sampleBedee}` : ''}`, { duration: 12000 });
            } else {
              toast.success(msg);
            }
          }
        } catch { clearInterval(poll); setSyncingPL(false); toast.error('Erreur suivi sync Pennylane'); }
      }, 2500);
    } catch (e) {
      setSyncingPL(false);
      toast.error(e.response?.data?.detail || 'Erreur sync tiers Pennylane');
    }
  };

  // ─── Détection doublons ──────────────────────────────────────────────────
  const detectDuplicates = async () => {
    if (!dupCriteria.length) { toast.error('Sélectionnez au moins un critère'); return; }
    setDupDetecting(true);
    setDupGroups([]);
    setDupMasters({});
    try {
      const r = await api.detectDuplicates(dupCriteria.join(','));
      const d = r.data;
      setDupGroups(d.groups || []);
      if (!d.groups?.length) toast.info('Aucun doublon trouvé avec ces critères');
      else toast.info(`${d.total_groups} groupe(s) de doublons trouvé(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur détection'); }
    finally { setDupDetecting(false); }
  };

  const handleMergeGroup = async (group) => {
    const masterId = dupMasters[group.key];
    if (!masterId) { toast.error('Choisissez la fiche maître'); return; }
    const dupIds = group.entreprises.filter(e => e.entreprise_id !== masterId).map(e => e.entreprise_id);
    if (dupIds.length > 1) {
      toast.error('La fusion est limitée à 2 fiches à la fois (contrainte Odoo). Fusionnez les doublons un par un depuis le tableau principal.');
      return;
    }
    setMerging(true);
    try {
      const r = await api.mergeEntreprises(masterId, dupIds);
      const odoo = r.data?.odoo_sync;
      const plConflict = r.data?.pl_conflict;
      if (odoo && !odoo.done && odoo.error && odoo.error !== 'sync_disabled') {
        toast.warning(`Fusion Bedee effectuée — mais la sync Odoo a échoué : ${odoo.error}. Veuillez fusionner manuellement dans Odoo.`);
      } else {
        toast.success(`Fusion effectuée — ${dupIds.length} doublon(s) supprimé(s)${odoo?.done ? ' (Odoo synchronisé ✓)' : ''}`);
      }
      if (plConflict?.length) {
        const noms = plConflict.map(e => `${e.nom} (${[e.numero_compte_client, e.numero_compte_fourn].filter(Boolean).join(' / ')})`).join(', ');
        toast.warning(
          `Conflit PennyLane : les comptes supplantés (${noms}) restent dans PL sur leurs anciens numéros. Créez une OD de régularisation si nécessaire.`,
          { duration: 8000 }
        );
      }
      setDupGroups(g => g.filter(gr => gr.key !== group.key));
      fetchEntreprises(1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur fusion'); }
    finally { setMerging(false); }
  };

  const handleMergeSelected = async () => {
    const ids = [...selectedEnt];
    if (ids.length < 2) { toast.error('Sélectionnez au moins 2 entreprises'); return; }
    setDupGroups([{ key: 'manual', count: ids.length, entreprises: entreprises.filter(e => ids.includes(e.entreprise_id)) }]);
    setDupMasters({});
    setDupPanel(true);
    setSelectedEnt(new Set());
  };

  const [promotingPer, setPromotingPer] = useState(false);
  const [mergingPer, setMergingPer] = useState(false);
  const [mergePerModal, setMergePerModal] = useState(null); // { personnes: [...], masterId: string }
  const [dupCheck, setDupCheck] = useState(null); // résultat check-duplicate dans le side-panel personne
  const dupCheckTimer = useRef(null);

  const handlePromotePer = async () => {
    if (!perPanel?.item) return;
    const fullName = [perPanel.item.prenom, perPanel.item.nom].filter(Boolean).join(' ');
    if (!window.confirm(`Promouvoir "${fullName}" en entreprise ?\n\nCette action :\n• Crée une fiche entreprise avec les informations du contact\n• Met à jour is_company dans Odoo (si synchronisé)\n• Retire ce contact des contrats de location concernés\n• Supprime définitivement la fiche contact\n\nCette action est irréversible.`)) return;
    setPromotingPer(true);
    try {
      const r = await api.promotePersonneToEntreprise(perPanel.item.personne_id);
      if (r.data.odoo_warning) {
        toast.warning(r.data.odoo_warning);
      } else {
        toast.success(`"${r.data.nom}" promu en entreprise avec succès`);
      }
      setPerPanel(null);
      fetchEntreprises(1);
      fetchPersonnes(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la promotion');
    } finally {
      setPromotingPer(false);
    }
  };


  const handleMergePersonnesSelected = () => {
    const ids = [...selectedPer];
    if (ids.length < 2) { toast.error('Sélectionnez au moins 2 contacts'); return; }
    const selectedPersonnes = personnes.filter(p => ids.includes(p.personne_id));
    setMergePerModal({ personnes: selectedPersonnes, masterId: selectedPersonnes[0].personne_id });
    setSelectedPer(new Set());
  };

  const confirmMergePersonnes = async () => {
    if (!mergePerModal?.masterId) { toast.error('Choisissez la fiche maître'); return; }
    const dupIds = mergePerModal.personnes
      .filter(p => p.personne_id !== mergePerModal.masterId)
      .map(p => p.personne_id);
    setMergingPer(true);
    try {
      await api.mergePersonnes(mergePerModal.masterId, dupIds);
      toast.success(`Fusion effectuée — ${dupIds.length} doublon(s) supprimé(s)`);
      setMergePerModal(null);
      fetchPersonnes(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur fusion');
    } finally {
      setMergingPer(false);
    }
  };

  const inp  = "w-full px-3 py-2 rounded-lg border text-sm outline-none transition-all focus:ring-2 focus:ring-[var(--primary-light)]";
  const inpSt = { borderColor: 'var(--border)', background: 'white', color: 'var(--text)' };
  const lbl   = "block text-xs font-semibold mb-1";
  const lblSt = { color: 'var(--text-muted)' };

  // Contacts liés à l'entreprise en cours d'édition (chargés via API dédiée)
  const entContacts = entContactsList;

  // Entreprises liées au contact en cours d'édition
  // linkedEntsMap = Map<entreprise_id, {entreprise_id, nom, types_entreprise}>
  // Alimenté depuis item.entreprises_liees (backend) + ajouts manuels (handleAddPerEnt)
  const [linkedEntsMap, setLinkedEntsMap] = useState({});

  const perLinkedEnts = useMemo(() => {
    const ids = perForm.entreprise_ids || [];
    return ids
      .map(id => {
        // Priorité : cache local (noms complets provenant du backend ou d'une sélection)
        if (linkedEntsMap[id]) return linkedEntsMap[id];
        // Fallback sur la liste paginée si disponible
        const found = entreprises.find(e => e.entreprise_id === id);
        if (found) return found;
        // Fallback minimaliste sur le nom de l'entreprise principale du contact
        if (id === perPanel?.item?.entreprise_id && perPanel?.item?.entreprise_nom)
          return { entreprise_id: id, nom: perPanel.item.entreprise_nom, types_entreprise: [] };
        return null;
      })
      .filter(Boolean);
  }, [perForm.entreprise_ids, linkedEntsMap, entreprises, perPanel]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="flex-1 flex flex-col" style={{ background: 'var(--bg)', minHeight: 0 }}>

      {/* ── Bannière verrouillage migration Pennylane ───────────────────────── */}
      {annuaireLocked && (
        <div className="flex-none flex items-center gap-3 px-6 py-2.5" style={{ background: '#FEF2F2', borderBottom: '1px solid #FCA5A5' }}>
          <ShieldAlert size={15} style={{ color: '#DC2626', flexShrink: 0 }} />
          <p className="text-xs font-medium" style={{ color: '#991B1B' }}>
          <strong>Annuaire verrouillé — Migration Pennylane en cours.</strong> Toute création, modification, suppression et fusion d'entreprises est bloquée pour tous les utilisateurs.
          </p>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex-none px-4 sm:px-6 pt-4 sm:pt-6 pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Annuaire</h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
              {totalEnt} entreprise{totalEnt !== 1 ? 's' : ''} · {totalPer} contact{totalPer !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Boutons secondaires — masqués sur mobile */}
            <div className="hidden sm:flex items-center gap-2 flex-wrap">
              {activeTab === 'entreprises' && selectedEnt.size === 2 && (
                <button onClick={handleMergeSelected}
                  data-testid="merge-selected-btn"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                  style={{ border: '1px solid #D97706', color: '#D97706', background: '#FFFBEB' }}>
                  <Merge size={15} /> Fusionner (2)
                </button>
              )}
              {activeTab === 'personnes' && selectedPer.size === 2 && (
                <button onClick={handleMergePersonnesSelected}
                  data-testid="merge-persons-btn"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                  style={{ border: '1px solid #D97706', color: '#D97706', background: '#FFFBEB' }}>
                  <Merge size={15} /> Fusionner (2)
                </button>
              )}
              <button onClick={() => setDupPanel(true)} data-testid="detect-duplicates-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                style={{ border: '1px solid var(--border)', color: 'var(--text-muted)', background: 'white' }}>
                <SlidersHorizontal size={15} /> Doublons
              </button>

              {user?.role === 'super_admin' && (
                <button
                  onClick={handleSyncPLTiers}
                  disabled={syncingPL}
                  data-testid="sync-pl-tiers-btn"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ border: '1px solid #0EA5E9', color: '#0EA5E9', background: '#F0F9FF' }}>
                  {syncingPL
                    ? <span className="w-3.5 h-3.5 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
                    : <RefreshCw size={15} />}
                  Sync tiers Pennylane
                </button>
              )}
            </div>
            {/* Bouton Créer — toujours visible */}
            <button onClick={() => {
                if (activeTab === 'entreprises') openEntPanel(null);
                else { setDupCheck(null); openPerPanel(null); }
              }}
              data-testid="add-annuaire-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ background: 'var(--primary-gradient)' }}>
              <Plus size={16} />
              <span className="hidden sm:inline">
                {activeTab === 'entreprises' ? 'Nouvelle entreprise' : 'Nouvelle personne'}
              </span>
              <span className="sm:hidden">
                {activeTab === 'entreprises' ? 'Entreprise' : 'Personne'}
              </span>
            </button>
          </div>
        </div>

        {/* Tabs + Filtres */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-4">
          {/* Tabs */}
          <div className="flex rounded-xl p-1 self-start sm:self-auto" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
            {[
              { key: 'entreprises', label: `Entreprises${totalEnt > 0 ? ` (${totalEnt})` : ''}`, icon: Building2 },
              { key: 'personnes',   label: `Personnes${totalPer > 0 ? ` (${totalPer})` : ''}`,   icon: Users },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold transition-all"
                style={{ background: activeTab === t.key ? 'white' : 'transparent', color: activeTab === t.key ? 'var(--primary-light)' : 'var(--text-muted)', boxShadow: activeTab === t.key ? '0 1px 4px rgba(0,0,0,.06)' : 'none' }}>
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </div>

          {/* Search — pleine largeur sur mobile */}
          <div className="relative flex-1 w-full sm:max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-placeholder)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm outline-none"
              style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }} />
          </div>

          {/* Filtre type (entreprises seulement) — scroll horizontal sur mobile */}
          {activeTab === 'entreprises' && (
            <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0 sm:flex-wrap items-center" style={{ scrollbarWidth: 'none' }}>
              {[{ value: '', label: 'Tous' }, ...TYPE_LIST].map(t => (
                <button key={t.value || 'all'} onClick={() => setTypeFilter(t.value)}
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                  style={{ background: typeFilter === t.value ? (t.bg || 'var(--primary-50)') : 'white', color: typeFilter === t.value ? (t.color || 'var(--primary-light)') : 'var(--text-muted)', border: `1px solid ${typeFilter === t.value ? (t.color || 'var(--primary-light)') : 'var(--border)'}` }}>
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Filtre par tags — visible sur les deux onglets, contextualisé par portée */}
          {(() => {
            const tabTags = allCatalogTags.filter(t =>
              activeTab === 'entreprises' ? t.pour_entreprise : t.pour_personne
            );
            if (!tabTags.length) return null;
            return (
              <div className="relative flex-shrink-0" ref={tagDropdownRef}>
                <button
                  data-testid="btn-filtre-tags"
                  onClick={() => setTagDropdownOpen(v => !v)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all"
                  style={{
                    background:   tagFiltersSelected.size > 0 ? 'var(--primary-50)' : 'white',
                    color:        tagFiltersSelected.size > 0 ? 'var(--primary-solid)' : 'var(--text-muted)',
                    borderColor:  tagFiltersSelected.size > 0 ? 'var(--primary-solid)' : 'var(--border)',
                  }}>
                  <Filter size={12} />
                  Tags
                  {tagFiltersSelected.size > 0 && (
                    <span style={{
                      background: 'var(--primary-solid)', color: '#fff',
                      borderRadius: 99, padding: '1px 6px',
                      fontSize: 10, fontWeight: 700, lineHeight: '14px',
                    }}>
                      {tagFiltersSelected.size}
                    </span>
                  )}
                  <ChevronDown size={11} style={{ transform: tagDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                </button>
                {tagDropdownOpen && (
                  <div className="absolute z-50 mt-1 rounded-xl shadow-lg border bg-white min-w-[220px] max-h-[380px] overflow-y-auto"
                    style={{ borderColor: 'var(--border)', top: '100%', left: 0 }}>
                    {TAG_FILTER_GROUPS.map(group => {
                      const groupTags = tabTags.filter(t => t.tag_type === group.key);
                      if (!groupTags.length) return null;
                      return (
                        <div key={String(group.key)}>
                          <div className="flex items-center gap-1.5 px-3 py-1.5"
                            style={{ background: group.bg, borderBottom: '1px solid var(--border)', borderTop: '1px solid var(--border)' }}>
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: group.iconColor }} />
                            <span className="text-xs font-semibold" style={{ color: group.iconColor }}>{group.label}</span>
                          </div>
                          {groupTags.map(t => (
                            <label key={t.tag_id}
                              className="flex items-center gap-2.5 px-4 py-2 cursor-pointer hover:bg-gray-50"
                              style={{ color: 'var(--text)' }}>
                              <input type="checkbox" className="w-3.5 h-3.5 accent-indigo-600 flex-shrink-0"
                                checked={tagFiltersSelected.has(t.tag_id)}
                                onChange={() => toggleTagFilter(t.tag_id)}
                                data-testid={`tag-filter-${t.tag_id}`} />
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t.couleur }} />
                              <span className="text-xs font-medium">{t.nom}</span>
                            </label>
                          ))}
                        </div>
                      );
                    })}
                    {tagFiltersSelected.size > 0 && (
                      <div className="border-t px-4 py-2" style={{ borderColor: 'var(--border)' }}>
                        <button onClick={() => { setTagFiltersSelected(new Set()); setTagDropdownOpen(false); }}
                          className="text-xs font-medium"
                          style={{ color: '#DC2626' }}>
                          Effacer les filtres ({tagFiltersSelected.size})
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Filtre : afficher les archivées — onglet entreprises uniquement */}
          {activeTab === 'entreprises' && (
            <button
              data-testid="btn-toggle-archives"
              onClick={() => { setShowArchived(v => !v); setPageEnt(1); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all flex-shrink-0"
              style={{
                background:  showArchived ? '#FEF3C7' : 'white',
                color:       showArchived ? '#92400E'  : 'var(--text-muted)',
                borderColor: showArchived ? '#FDE68A'  : 'var(--border)',
              }}
              title={showArchived ? 'Masquer les entreprises archivées' : 'Afficher les entreprises archivées'}
            >
              <Archive size={12} />
              Archivées
            </button>
          )}

          {/* Pills des tags actifs — même ligne, alignées à droite */}
          {tagFiltersSelected.size > 0 && (() => {
            const tabTags = allCatalogTags.filter(t =>
              activeTab === 'entreprises' ? t.pour_entreprise : t.pour_personne
            );
            const activeTags = tabTags.filter(t => tagFiltersSelected.has(t.tag_id));
            if (!activeTags.length) return null;
            return (
              <div className="flex items-center gap-1.5 flex-shrink-0 overflow-x-auto"
                style={{ marginLeft: 'auto', scrollbarWidth: 'none' }}>
                {activeTags.map(t => (
                  <span key={t.tag_id}
                    data-testid={`active-tag-pill-${t.tag_id}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
                      padding: '3px 7px 3px 7px', borderRadius: 99,
                      background: t.couleur ? `${t.couleur}22` : '#F3F4F6',
                      border: `1px solid ${t.couleur || '#D1D5DB'}`,
                      fontSize: 11, fontWeight: 600, color: t.couleur || '#374151',
                      whiteSpace: 'nowrap',
                    }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.couleur, flexShrink: 0 }} />
                    {t.nom}
                    <button onClick={() => toggleTagFilter(t.tag_id)}
                      data-testid={`remove-tag-pill-${t.tag_id}`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                        lineHeight: 1, color: t.couleur || '#6B7280', display: 'flex', alignItems: 'center' }}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            );
          })()}

        </div>
      </div>
      {activeTab === 'entreprises' && (
        <div ref={entScrollRef} className="mx-3 sm:mx-6 mb-6 overflow-auto rounded-xl"
          style={{ border: '1px solid var(--border)', background: 'white', height: 'calc(100vh - 200px)', opacity: fetchingEnt ? 0.6 : 1, transition: 'opacity 0.15s' }}>
          <table className="border-collapse" style={{ fontSize: 13, width: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 44 }} />       {/* checkbox */}
              <col style={{ width: 220 }} />      {/* Nom */}
              <col style={{ width: 100 }} />      {/* SIREN */}
              <col style={{ width: 160 }} />      {/* Types */}
              <col style={{ width: 110 }} />      {/* Ville */}
              <col style={{ width: 130 }} />      {/* Téléphone */}
              <col style={{ width: 180 }} />      {/* Email */}
              <col style={{ width: 70 }} />       {/* Contacts */}
              <col style={{ width: 120 }} />      {/* Statut */}
            </colgroup>
            <thead>
              <tr>
                <th className="px-3 py-3" style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                  <input type="checkbox"
                    checked={selectedEnt.size === filteredEnt.length && filteredEnt.length > 0}
                    onChange={e => setSelectedEnt(e.target.checked ? new Set(filteredEnt.map(x => x.entreprise_id)) : new Set())}
                    className="w-4 h-4 rounded" style={{ accentColor: 'var(--primary-light)' }} />
                </th>
                {[
                  { label: 'Nom' }, { label: 'SIREN' }, { label: 'Types' }, { label: 'Ville' },
                  { label: 'Téléphone' }, { label: 'Email' }, { label: 'Contacts' },
                  { label: 'Statut' },
                ].map(h => (
                  <th key={h.label} className="px-3 py-3 text-left text-xs font-semibold"
                    style={{ color: 'var(--text-muted)', position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(loading || fetchingEnt) && (
                <tr><td colSpan={9} className="text-center py-16 text-sm" style={{ color: 'var(--text-placeholder)' }}>Chargement…</td></tr>
              )}
              {!loading && !fetchingEnt && filteredEnt.length === 0 && (
                <tr><td colSpan={9} className="text-center py-16 text-sm" style={{ color: 'var(--text-placeholder)' }}>Aucune entreprise</td></tr>
              )}
              {!loading && !fetchingEnt && filteredEnt.length > 0 && (() => {
                const vItems = entVirtualizer.getVirtualItems();
                const totalSize = entVirtualizer.getTotalSize();
                const paddingTop    = vItems.length > 0 ? vItems[0].start : 0;
                const paddingBottom = vItems.length > 0 ? totalSize - vItems[vItems.length - 1].end : totalSize;
                return (
                  <>
                    {paddingTop > 0 && <tr><td colSpan={9} style={{ height: paddingTop, padding: 0 }} /></tr>}
                    {vItems.map(vRow => {
                      const e = filteredEnt[vRow.index];
                      const nbContacts = e.contacts_count ?? entContactCountMap.get(e.entreprise_id) ?? 0;
                      const isSel = selectedEnt.has(e.entreprise_id);
                      // Couleur de fond selon statut
                      const rowBg = e.archive ? '#FFFBEB' : e.contentieux ? '#FFF7ED' : (e.gele ? '#F0F9FF' : (isSel ? '#FAF5FF' : 'white'));
                      return (
                        <tr key={e.entreprise_id}
                          onClick={() => openEntPanel(e)}
                          style={{ borderBottom: '1px solid var(--border)', background: rowBg, cursor: 'pointer', transition: 'background .15s' }}
                          className="hover:bg-gray-50">
                          <td className="px-3 py-3" onClick={ev => ev.stopPropagation()}>
                            <input type="checkbox" checked={isSel}
                              onChange={ev => { const s = new Set(selectedEnt); ev.target.checked ? s.add(e.entreprise_id) : s.delete(e.entreprise_id); setSelectedEnt(s); }}
                              className="w-4 h-4 rounded" style={{ accentColor: 'var(--primary-light)' }} />
                          </td>
                          <td className="px-3 py-3 font-semibold" style={{ color: 'var(--text)', overflow: 'hidden' }}>
                            <span className="truncate block" title={e.nom}>{e.nom}</span>
                          </td>
                          <td className="px-3 py-3" style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>
                            {e.siren || '—'}
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(e.types_entreprise || []).map(t => <TypeBadge key={t} type={t} />)}
                            </div>
                          </td>
                          <td className="px-3 py-3" style={{ color: 'var(--text-muted)' }}>{e.ville || '—'}</td>
                          <td className="px-3 py-3" style={{ color: 'var(--text-muted)' }}>{formatPhone(e.telephone) || '—'}</td>
                          <td className="px-3 py-3" style={{ color: 'var(--text-muted)' }}>{e.email || '—'}</td>
                          <td className="px-3 py-3">
                            {nbContacts > 0 ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                                style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>
                                <Users size={11} /> {nbContacts}
                              </span>
                            ) : <span style={{ color: 'var(--text-placeholder)' }}>—</span>}
                          </td>
                          {/* Colonne Statut */}
                          <td className="px-3 py-3">
                            {e.archive ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#F3F4F6', color: '#6B7280' }}><Archive size={10} /> Archivé</span>
                            ) : e.contentieux ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#FEF3C7', color: '#B45309' }}><Scale size={10} /> Contentieux</span>
                            ) : e.gele ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#DBEAFE', color: '#0369A1' }}><Snowflake size={10} /> Gelé</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#D1FAE5', color: '#059669' }}><CheckCircle size={10} /> Actif</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {paddingBottom > 0 && <tr><td colSpan={9} style={{ height: paddingBottom, padding: 0 }} /></tr>}
                  </>
                );
              })()}
            </tbody>
          </table>
          {/* Footer pagination entreprises */}
          {(totalEnt > 0) && (
            <div className="px-4 py-2.5 flex items-center justify-between sticky bottom-0"
              style={{ borderTop: '1px solid var(--border)', background: 'white' }}>
              <span className="text-xs" style={{ color: 'var(--text-placeholder)' }}>
                {fetchingEnt ? 'Chargement…' : `${Math.min((pageEnt - 1) * pageSize + 1, totalEnt)}–${Math.min(pageEnt * pageSize, totalEnt)} sur ${totalEnt} entreprise${totalEnt !== 1 ? 's' : ''}`}
                {selectedEnt.size > 0 ? ` · ${selectedEnt.size} sélectionnée(s)` : ''}
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPageEnt(1)} disabled={pageEnt === 1}
                  className="px-2 py-1 text-xs rounded disabled:opacity-40" style={{ background: 'var(--border)' }}>«</button>
                <button onClick={() => setPageEnt(p => Math.max(1, p - 1))} disabled={pageEnt === 1}
                  className="px-2 py-1 text-xs rounded disabled:opacity-40" style={{ background: 'var(--border)' }}>‹</button>
                <span className="text-xs px-2" style={{ color: 'var(--text-muted)' }}>{pageEnt} / {totalPagesEnt}</span>
                <button onClick={() => setPageEnt(p => Math.min(totalPagesEnt, p + 1))} disabled={pageEnt === totalPagesEnt}
                  className="px-2 py-1 text-xs rounded disabled:opacity-40" style={{ background: 'var(--border)' }}>›</button>
                <button onClick={() => setPageEnt(totalPagesEnt)} disabled={pageEnt === totalPagesEnt}
                  className="px-2 py-1 text-xs rounded disabled:opacity-40" style={{ background: 'var(--border)' }}>»</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Table Contacts ───────────────────────────────────────────────── */}
      {activeTab === 'personnes' && (
        <div ref={perScrollRef} className="mx-3 sm:mx-6 mb-6 overflow-auto rounded-xl"
          style={{ border: '1px solid var(--border)', background: 'white', height: 'calc(100vh - 200px)', opacity: fetchingPer ? 0.6 : 1, transition: 'opacity 0.15s' }}>
          <table className="border-collapse" style={{ fontSize: 13, width: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 44 }} />
              <col style={{ width: 180 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 44 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="px-3 py-3" style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                  <input type="checkbox"
                    checked={selectedPer.size === filteredPer.length && filteredPer.length > 0}
                    onChange={e => setSelectedPer(e.target.checked ? new Set(filteredPer.map(x => x.personne_id)) : new Set())}
                    className="w-4 h-4 rounded" style={{ accentColor: 'var(--primary-light)' }} />
                </th>
                {['Nom', 'Fonctions', 'Casquette', 'Téléphone', 'Email', ''].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-semibold"
                    style={{ color: 'var(--text-muted)', position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(loading || fetchingPer) && (
                <tr><td colSpan={7} className="text-center py-16 text-sm" style={{ color: 'var(--text-placeholder)' }}>Chargement…</td></tr>
              )}
              {!loading && !fetchingPer && filteredPer.length === 0 && (
                <tr><td colSpan={7} className="text-center py-16 text-sm" style={{ color: 'var(--text-placeholder)' }}>Aucun contact</td></tr>
              )}
              {!loading && !fetchingPer && filteredPer.length > 0 && (() => {
                const vItems = perVirtualizer.getVirtualItems();
                const totalSize = perVirtualizer.getTotalSize();
                const paddingTop    = vItems.length > 0 ? vItems[0].start : 0;
                const paddingBottom = vItems.length > 0 ? totalSize - vItems[vItems.length - 1].end : totalSize;
                return (
                  <>
                    {paddingTop > 0 && <tr><td colSpan={7} style={{ height: paddingTop, padding: 0 }} /></tr>}
                    {vItems.map(vRow => {
                      const p = filteredPer[vRow.index];
                      const isSel = selectedPer.has(p.personne_id);
                      const fullName = [p.prenom, p.nom].filter(Boolean).join(' ');
                      return (
                        <tr key={p.personne_id}
                          onClick={() => openPerPanel(p)}
                          style={{ borderBottom: '1px solid var(--border)', background: isSel ? '#FAF5FF' : 'white', cursor: 'pointer' }}
                          className="hover:bg-gray-50">
                          <td className="px-3 py-3" onClick={ev => ev.stopPropagation()}>
                            <input type="checkbox" checked={isSel}
                              onChange={ev => { const s = new Set(selectedPer); ev.target.checked ? s.add(p.personne_id) : s.delete(p.personne_id); setSelectedPer(s); }}
                              className="w-4 h-4 rounded" style={{ accentColor: 'var(--primary-light)' }} />
                          </td>
                          <td className="px-3 py-3 font-semibold" style={{ color: 'var(--text)' }}>{fullName || '—'}</td>
                          <td className="px-3 py-3">
                            {(() => {
                              const cap = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
                              const fonctionTags = (p.personne_tags || []).filter(t => t.tag_type === 'fonction');
                              if (fonctionTags.length > 0) {
                                return fonctionTags.map(t => (
                                  <span key={t.tag_id} className="text-xs px-2 py-0.5 rounded-full mr-1"
                                        style={{ background: t.couleur + '22', color: t.couleur, border: `1px solid ${t.couleur}55` }}>
                                    {cap(t.nom)}
                                  </span>
                                ));
                              }
                              return (p.fonctions || []).map(f => (
                                <span key={f} className="text-xs px-2 py-0.5 rounded-full mr-1"
                                      style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>{f}</span>
                              ));
                            })()}
                          </td>
                          {/* ── Casquette : contexte de la personne ───────────────────────── */}
                          <td className="px-3 py-3">
                            {(() => {
                              const primaryNom = entByIdMap.get(p.entreprise_id)?.nom || p.entreprise_nom;
                              const extraIds = (p.entreprise_ids || []).filter(id => id !== p.entreprise_id);
                              const extraEnts = extraIds.map(id => entByIdMap.get(id)).filter(Boolean);
                              const hasCompany = primaryNom || extraEnts.length > 0;
                              return (
                                <div className="flex flex-wrap gap-1">
                                  {!hasCompany && (
                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
                                      style={{ background: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0' }}>
                                      <User size={10} /> Particulier B2C
                                    </span>
                                  )}
                                  {primaryNom && (
                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                                      style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                                      <Building2 size={10} /> {primaryNom}
                                    </span>
                                  )}
                                  {extraEnts.map(e => (
                                    <span key={e.entreprise_id} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                                      style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                                      <Building2 size={10} /> {e.nom}
                                    </span>
                                  ))}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-3" style={{ color: 'var(--text-muted)' }}>{formatPhone(p.telephone_fixe || p.telephone_mobile) || '—'}</td>
                          <td className="px-3 py-3" style={{ color: 'var(--text-muted)' }}>{p.email || '—'}</td>
                          <td className="px-3 py-3" onClick={ev => ev.stopPropagation()}>
                            <button onClick={() => { if (window.confirm('Supprimer ?')) deletePer(p.personne_id); }}
                              className="p-1.5 rounded-lg hover:bg-red-50 transition-all" style={{ color: '#ef4444' }}>
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {paddingBottom > 0 && <tr><td colSpan={7} style={{ height: paddingBottom, padding: 0 }} /></tr>}
                  </>
                );
              })()}
            </tbody>
          </table>
          {/* Footer pagination contacts */}
          {(totalPer > 0) && (
            <div className="px-4 py-2.5 flex items-center justify-between sticky bottom-0"
              style={{ borderTop: '1px solid var(--border)', background: 'white' }}>
              <span className="text-xs" style={{ color: 'var(--text-placeholder)' }}>
                {fetchingPer ? 'Chargement…' : `${Math.min((pagePer - 1) * pageSize + 1, totalPer)}–${Math.min(pagePer * pageSize, totalPer)} sur ${totalPer} personne${totalPer !== 1 ? 's' : ''}`}
                {selectedPer.size > 0 ? ` · ${selectedPer.size} sélectionné(s)` : ''}
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPagePer(1)} disabled={pagePer === 1}
                  className="px-2 py-1 text-xs rounded disabled:opacity-40" style={{ background: 'var(--border)' }}>«</button>
                <button onClick={() => setPagePer(p => Math.max(1, p - 1))} disabled={pagePer === 1}
                  className="px-2 py-1 text-xs rounded disabled:opacity-40" style={{ background: 'var(--border)' }}>‹</button>
                <span className="text-xs px-2" style={{ color: 'var(--text-muted)' }}>{pagePer} / {totalPagesPer}</span>
                <button onClick={() => setPagePer(p => Math.min(totalPagesPer, p + 1))} disabled={pagePer === totalPagesPer}
                  className="px-2 py-1 text-xs rounded disabled:opacity-40" style={{ background: 'var(--border)' }}>›</button>
                <button onClick={() => setPagePer(totalPagesPer)} disabled={pagePer === totalPagesPer}
                  className="px-2 py-1 text-xs rounded disabled:opacity-40" style={{ background: 'var(--border)' }}>»</button>
              </div>
            </div>
          )}
        </div>
      )}


      {/* ═══ PANNEAU ENTREPRISE ════════════════════════════════════════════ */}
      <SidePanel
        open={!!entPanel}
        onOpenChange={(v) => { if (!v) setEntPanel(null); }}
        title={entPanel?.item ? entPanel.item.nom : 'Nouvelle entreprise'}
        copyValue={entPanel?.item?.nom}
        size="xl"
        noPadding
        stackedBehind={!!perPanel}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

          {/* ── Onglets ──────────────────────────────────────────────────── */}
          <div style={{ flexShrink: 0, borderBottom: '1px solid var(--border)', background: 'white', paddingLeft: 24, paddingRight: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex' }}>
              {[
                { key: 'infos', label: 'Informations' },
                { key: 'contacts', label: `Contacts${entContacts.length > 0 ? ` (${entContacts.length})` : ''}` },
                { key: 'compta', label: 'Compta client' },
                { key: 'compta_fourn', label: 'Compta fournisseur' },
                { key: 'conditions', label: 'Conditions commerciales' },
                { key: 'materiels', label: 'Matériels', icon: Package },
                { key: 'documents', label: 'Documents', icon: FolderOpen, count: entDocCount },
                { key: 'discussions', label: 'Activités' },
                { key: 'appetences', label: 'Appétences', icon: TrendingUp },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setEntTab(tab.key);
                    if (tab.key === 'compta' && entPanel?.item && !entCompta.data?.groups && !entCompta.loading) {
                      loadEntCompta(entPanel.item.entreprise_id);
                    }
                    if (tab.key === 'compta_fourn' && entPanel?.item && !entComptaFourn.data?.groups && !entComptaFourn.loading) {
                      loadEntComptaFourn(entPanel.item.entreprise_id);
                    }
                    if (tab.key === 'materiels' && entPanel?.item && !entMateriels.data?.length && !entMateriels.loading) {
                      loadEntMateriels(entPanel.item.entreprise_id);
                    }
                    if (tab.key === 'appetences' && entPanel?.item && !entAppetences.data?.length && !entAppetences.loading) {
                      loadEntAppetences(entPanel.item.entreprise_id);
                    }
                  }}
                  style={{
                    padding: '10px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    border: 'none',
                    borderBottom: `2px solid ${entTab === tab.key ? 'var(--primary-light)' : 'transparent'}`,
                    marginBottom: -1,
                    background: 'transparent',
                    color: entTab === tab.key ? 'var(--primary-light)' : 'var(--text-muted)',
                    cursor: 'pointer',
                    transition: 'color .15s',
                  }}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-xs font-bold text-white"
                      style={{ background: 'var(--primary-light)', fontSize: 10 }}>
                      {tab.count}
                    </span>
                  )}
                  {tab.key === 'discussions' && hasActivity && <ActivityPulse />}
                </button>
              ))}
              </div>
              {/* Boutons Reminder + Discussion — droite */}
              <div className="flex items-center gap-2">
              </div>
              {/* Boutons Geler et Contentieux supprimés - gestion via Tags */}
            </div>
          </div>

          {/* ── Bannière Archivé ───────────────────────────────────────────── */}
          {entPanel?.item?.archive && (
            <div style={{ background: '#FEF3C7', borderBottom: '1px solid #FDE68A', padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <Archive size={14} style={{ color: '#92400E', flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: '#92400E' }}>Contact archivé</span>
              <span style={{ fontSize: 12, color: '#92400E' }}>— Ce contact est masqué de toutes les listes. Il peut être désarchivé à tout moment.</span>
            </div>
          )}

          {/* Bandeaux supprimés - Tags maintenant intégrés dans le formulaire */}

          {/* ── Contenu ──────────────────────────────────────────────────── */}
          <div style={{ flex: 1, overflowY: 'auto' }}>

            {entTab === 'infos' && (
              <div className="px-6 py-5">

                {/* ── Pappers + Tags ──────────────────────────────────────── */}
                <div className="space-y-3">
                  {/* Ligne : Pappers (50%) + Tags (50%) */}
                  <div className="flex items-start gap-3">
                    {/* Widget Pappers (moitié gauche) */}
                    <div className="flex-1 relative">
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl border"
                        style={{ borderColor: '#6366F1', background: '#F5F3FF' }}>
                        <Sparkles size={14} style={{ color: '#6366F1', flexShrink: 0 }} />
                        <input
                          value={pappersQuery}
                          onChange={e => handlePappersInput(e.target.value)}
                          placeholder="SIREN (9 chiffres)"
                          className="flex-1 bg-transparent text-sm outline-none"
                          style={{ color: '#3730A3' }}
                          maxLength={9}
                          inputMode="numeric"
                          data-testid="pappers-search-input"
                        />
                        {pappersLoading && <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />}
                        {pappersQuery && !pappersLoading && (
                          <button onClick={() => setPappersQuery('')}>
                            <X size={13} style={{ color: '#6366F1' }} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Widget Tags (moitié droite) avec dropdown multi-select */}
                    {entPanel?.item && (
                      <div className="flex-1 relative">
                        <div className="relative" ref={tagAddMenuRef}>
                          <button
                            data-testid="btn-open-tag-dropdown"
                            onClick={() => setShowTagAddMenu(v => !v)}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border transition-all"
                            style={{ 
                              borderColor: entAppliedTags.length > 0 ? '#6366F1' : 'var(--border)', 
                              background: entAppliedTags.length > 0 ? '#F5F3FF' : 'white',
                              color: 'var(--text)' 
                            }}>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Tag size={14} style={{ color: entAppliedTags.length > 0 ? '#6366F1' : 'var(--text-muted)', flexShrink: 0 }} />
                              {entAppliedTags.length === 0 ? (
                                <span className="text-sm" style={{ color: 'var(--text-placeholder)' }}>Aucun tag</span>
                              ) : (
                                <div className="flex flex-wrap gap-1 flex-1 min-w-0">
                                  {entAppliedTags.slice(0, 2).map(t => (
                                    <span key={t.tag_id} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold"
                                      style={{ background: t.couleur + '30', color: t.couleur }}>
                                      {t.nom}
                                    </span>
                                  ))}
                                  {entAppliedTags.length > 2 && (
                                    <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                                      +{entAppliedTags.length - 2}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <ChevronDown size={14} style={{ color: 'var(--text-muted)', flexShrink: 0, transition: 'transform 0.15s', transform: showTagAddMenu ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                          </button>
                          
                          {showTagAddMenu && (
                            <div className="absolute z-50 mt-1 rounded-xl shadow-lg border bg-white py-2 w-full"
                              style={{ borderColor: 'var(--border)', top: '100%', left: 0, maxHeight: '300px', overflowY: 'auto' }}>
                              {allCatalogTags.filter(t => (t.pour_tiers ?? t.pour_entreprise) && t.can_use !== false).map(t => {
                                const isApplied = entAppliedTags.some(at => at.tag_id === t.tag_id);
                                return (
                                  <label key={t.tag_id}
                                    className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-50 text-sm"
                                    style={{ color: 'var(--text)' }}>
                                    <input 
                                      type="checkbox" 
                                      className="w-3.5 h-3.5 accent-indigo-600"
                                      checked={isApplied}
                                      onChange={() => isApplied ? handleRemoveTag(t.tag_id) : handleApplyTag(t.tag_id)}
                                      data-testid={`tag-checkbox-${t.tag_id}`}
                                    />
                                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                      style={{ background: t.couleur }} />
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-xs">{t.nom}</div>
                                      {t.description && (
                                        <div className="text-xs mt-0.5 leading-tight truncate"
                                          style={{ color: 'var(--text-muted)' }}>
                                          {t.description}
                                        </div>
                                      )}
                                    </div>
                                  </label>
                                );
                              })}
                              {allCatalogTags.filter(t => (t.pour_tiers ?? t.pour_entreprise) && t.can_use !== false).length === 0 && (
                                <div className="px-4 py-3 text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                                  Aucun tag disponible
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Alerte procédure collective */}
                  {pappersProc && (
                    <div className="flex items-start gap-3 px-4 py-3 rounded-xl border"
                      style={{ background: '#FEF2F2', borderColor: '#FECACA' }}>
                      <AlertCircle size={16} style={{ color: '#DC2626', flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <p className="text-sm font-semibold" style={{ color: '#DC2626' }}>
                          Procédure collective — {pappersProc.type}
                        </p>
                        {pappersProc.date && (
                          <p className="text-xs mt-0.5" style={{ color: '#B91C1C' }}>
                            Jugement : {pappersProc.date}{pappersProc.tribunal ? ` · ${pappersProc.tribunal}` : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Nom + Types */}
                  <div className="flex items-center gap-3">
                    <input value={entForm.nom} onChange={e => setEntForm(f => ({ ...f, nom: e.target.value }))}
                      placeholder="Nom de l'entreprise *" className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} data-testid="ent-nom-input" />
                    <div className="flex flex-wrap gap-1.5 shrink-0">
                      {TYPE_LIST.map(t => {
                        const active = (entForm.types_entreprise || []).includes(t.value);
                        return (
                          <button key={t.value} type="button"
                            onClick={() => setEntForm(f => ({ ...f, types_entreprise: active ? f.types_entreprise.filter(x => x !== t.value) : [...f.types_entreprise, t.value] }))}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all"
                            style={{ background: active ? t.bg : 'white', color: active ? t.color : 'var(--text-muted)', borderColor: active ? t.color : 'var(--border)' }}>
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* ── Contacts ─────────────────────────────────────────────── */}
                <div className="space-y-3 mt-8">
                  <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Contacts</p>
                  <div className="grid grid-cols-3 gap-6">
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>Email :</label>
                      <input value={entForm.email} onChange={e => setEntForm(f => ({ ...f, email: e.target.value }))}
                        type="email" placeholder="contact@…" className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>Tél. :</label>
                      <PhoneInput value={entForm.telephone} onChange={e => setEntForm(f => ({ ...f, telephone: e.target.value }))}
                        className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>Web :</label>
                      <input value={entForm.site_web} onChange={e => setEntForm(f => ({ ...f, site_web: e.target.value }))}
                        placeholder="https://…" className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} />
                    </div>
                  </div>
                </div>

                {/* ── Adresse + Coordonnées bancaires ──────────────────── */}
                <div className="mt-8 space-y-3">
                  {/* Titres */}
                  <div className="grid grid-cols-3 gap-6">
                    <div className="col-span-2">
                      <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Adresse</p>
                    </div>
                    <div className="col-span-1 flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Coordonnées bancaires</p>
                      <button type="button" onClick={() => setShowIbansModal(true)}
                        className="flex items-center gap-1 transition-colors"
                        style={{ fontSize: 11, fontWeight: 600, color: 'var(--primary-solid)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                        <CreditCard size={11} />
                        {(entForm.ibans || []).length === 0 ? 'Ajouter' : 'Gérer'}
                      </button>
                    </div>
                  </div>
                  {/* Ligne 1 : Adresse | IBANs avec cadres */}
                  <div className="grid grid-cols-3 gap-6 items-center">
                    <div className="col-span-2 flex items-center gap-2">
                      <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>Adresse :</label>
                      <input value={entForm.adresse} onChange={e => setEntForm(f => ({ ...f, adresse: e.target.value }))}
                        placeholder="Rue, numéro…" className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} />
                    </div>
                    <div className="col-span-1 flex items-center gap-2">
                      <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>IBAN :</label>
                      <div style={{ flex: 1, minWidth: 0, position: 'relative', minHeight: 30 }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {(entForm.ibans || []).map((item, idx) => (
                            <div key={idx} className="rounded-lg px-2 py-1"
                              style={{ border: '1px solid var(--border)', background: 'var(--surface, #fff)' }}>
                              <span className="font-mono block" style={{ fontSize: 12, color: 'var(--text)', letterSpacing: '0.04em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {formatIban(item.iban)}
                              </span>
                              {item.nom_banque && (
                                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{item.nom_banque}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Ligne 2 : CP + Ville */}
                  <div className="grid grid-cols-3 gap-6 items-center">
                    <div className="col-span-2 grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>CP :</label>
                        <input value={entForm.code_postal} onChange={e => setEntForm(f => ({ ...f, code_postal: e.target.value }))}
                          className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 48, flexShrink: 0 }}>Ville :</label>
                        <input value={entForm.ville} onChange={e => setEntForm(f => ({ ...f, ville: e.target.value }))}
                          className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} />
                      </div>
                    </div>
                  </div>
                </div>

                {showIbansModal && (
                  <ModalGestionIbans
                    ibans={entForm.ibans || []}
                    onChange={arr => setEntForm(f => ({ ...f, ibans: arr }))}
                    onSave={saveEnt}
                    onClose={() => setShowIbansModal(false)}
                  />
                )}

                {/* ── Informations légales ─────────────────────────────────── */}
                <div className="space-y-3 mt-8">
                  <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Informations légales</p>
                  <div className="grid grid-cols-3 gap-6">
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>SIRET :</label>
                      <input value={fmtSiret(entForm.siret)}
                        onChange={e => setEntForm(f => ({ ...f, siret: e.target.value.replace(/\D/g, '') }))}
                        placeholder="XXX XXX XXX XXXXX" className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>SIREN :</label>
                      <input value={fmtSiren(entForm.siren || '')}
                        onChange={e => setEntForm(f => ({ ...f, siren: e.target.value.replace(/\D/g, '') }))}
                        placeholder="XXX XXX XXX" className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>TVA :</label>
                      <input value={entForm.tva_intra} onChange={e => setEntForm(f => ({ ...f, tva_intra: e.target.value }))}
                        placeholder="FR123…" className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>Forme :</label>
                      <input value={entForm.forme_juridique || ''} onChange={e => setEntForm(f => ({ ...f, forme_juridique: e.target.value }))}
                        placeholder="SARL, SAS…" className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} />
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>NAF :</label>
                      <input value={entForm.code_naf || ''} onChange={e => setEntForm(f => ({ ...f, code_naf: e.target.value }))}
                        placeholder="4311Z" className={inp} style={{ ...inpSt, width: 80, flexShrink: 0 }} />
                      <input value={entForm.libelle_naf || ''} onChange={e => setEntForm(f => ({ ...f, libelle_naf: e.target.value }))}
                        placeholder="Libellé activité" className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>Capital :</label>
                      <input value={capitalEditing ? (entForm.capital || '') : fmtCapitalDisplay(entForm.capital)}
                        onFocus={() => setCapitalEditing(true)}
                        onBlur={() => setCapitalEditing(false)}
                        onChange={e => {
                          const raw = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
                          setEntForm(f => ({ ...f, capital: raw }));
                        }}
                        placeholder="10 000" className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>Création :</label>
                      <input type="date" value={entForm.date_creation_societe || ''} onChange={e => setEntForm(f => ({ ...f, date_creation_societe: e.target.value }))}
                        className={inp} style={{ ...inpSt, flex: 1, minWidth: 0 }} />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-right" style={{ ...lblSt, width: 80, flexShrink: 0 }}>Pays :</label>
                      <PaysSelect value={entForm.pays_code || 'FR'} onChange={code => setEntForm(f => ({ ...f, pays_code: code }))}
                        className={inp} style={{ ...inpSt }} testid="entreprise-pays-select" />
                    </div>

                  </div>
                </div>

                {/* ── Notes ────────────────────────────────────────────────── */}
                <div className="mt-8">
                  <label className={lbl} style={lblSt}>Notes</label>
                  <textarea value={entForm.notes} onChange={e => setEntForm(f => ({ ...f, notes: e.target.value }))}
                    rows={3} placeholder="Informations complémentaires…"
                    className={inp} style={{ ...inpSt, resize: 'none' }} />
                </div>

                {/* Ancienne section Tags supprimée - désormais intégrée en haut avec Pappers */}

                {/* Dirigeants Pappers (lecture seule) */}
                {pappersDirigeants.length > 0 && (
                  <div className="rounded-xl border p-4 mt-4" style={{ borderColor: '#E0E7FF', background: '#F5F3FF' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles size={13} style={{ color: '#6366F1' }} />
                      <p className="text-xs font-semibold" style={{ color: '#4338CA' }}>
                        Dirigeants (source Pappers — lecture seule)
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      {pappersDirigeants.map((d, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{d.nom}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full"
                            style={{ background: '#E0E7FF', color: '#4338CA' }}>{d.qualite}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Assurance propre */}
                <div className="flex items-center justify-between px-4 py-3 rounded-xl border mt-4" style={{ borderColor: 'var(--border)', background: entForm.assurance_propre ? '#F0FDF4' : '#FFF7ED' }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Assurance des matériels loués :</p>
                    <p className="text-sm font-semibold" style={{ color: entForm.assurance_propre ? '#16A34A' : '#C2410C' }}>
                      {entForm.assurance_propre ? 'Ce client possède sa propre assurance' : "Ce client n'a pas d'assurance"}
                    </p>
                  </div>
                  <button type="button"
                    onClick={() => setEntForm(f => ({ ...f, assurance_propre: !f.assurance_propre }))}
                    data-testid="assurance-propre-toggle"
                    className="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ml-3"
                    style={{ background: entForm.assurance_propre ? '#16A34A' : '#C2410C' }}>
                    <span className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
                      style={{ transform: entForm.assurance_propre ? 'translateX(16px)' : 'translateX(0)' }} />
                  </button>
                </div>
              </div>
            )}

            {/* Onglet Contacts */}
            {entTab === 'contacts' && (
              <div className="px-6 py-5">
                {!entPanel?.item ? (
                  <p className="text-sm py-8 text-center" style={{ color: 'var(--text-placeholder)' }}>
                    Enregistrez d'abord l'entreprise pour lier des contacts.
                  </p>
                ) : (
                  <>
                  <div className="space-y-2 overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
                    {entContacts.length === 0 && (
                      <p className="text-sm py-6 text-center" style={{ color: 'var(--text-placeholder)' }}>
                        Aucun contact lié à cette entreprise.
                      </p>
                    )}
                    {entContacts.map(p => {
                      const cap = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
                      const ptags = p.personne_tags || [];
                      const fonctionTags  = ptags.filter(t => t.tag_type === 'fonction');
                      const autoTags      = ptags.filter(t => t.tag_type === 'automation');
                      const qualifTags    = ptags.filter(t => t.tag_type === 'qualification');
                      const Chips = ({ tags }) => tags.length > 0
                        ? <div className="flex flex-wrap gap-1">{tags.map(t => (
                            <span key={t.tag_id} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: t.couleur + '22', color: t.couleur, border: `1px solid ${t.couleur}55`, whiteSpace: 'nowrap' }}>
                              {cap(t.nom)}
                            </span>
                          ))}</div>
                        : <span className="text-xs" style={{ opacity: 0.3 }}>—</span>;
                      return (
                        <div key={p.personne_id}
                             onClick={() => openPerPanel(p)}
                             className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all hover:opacity-80"
                             style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', minWidth: 900 }}>
                          {/* Nom */}
                          <div style={{ width: 200, flexShrink: 0 }}>
                            <p className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>
                              {[p.prenom, p.nom].filter(Boolean).join(' ') || '—'}
                            </p>
                          </div>
                          {/* Fonctions */}
                          <div style={{ width: 170, flexShrink: 0 }} className="flex justify-center">
                            <Chips tags={fonctionTags} />
                          </div>
                          {/* Automatisations */}
                          <div style={{ width: 170, flexShrink: 0 }} className="flex justify-center">
                            <Chips tags={autoTags} />
                          </div>
                          {/* Qualifications */}
                          <div style={{ width: 170, flexShrink: 0 }} className="flex justify-center">
                            <Chips tags={qualifTags} />
                          </div>
                          {/* Téléphone */}
                          <div style={{ width: 150, flexShrink: 0 }}>
                            <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                              {p.telephone_mobile || <span style={{ opacity: 0.3 }}>—</span>}
                            </p>
                          </div>
                          {/* Email */}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                              {p.email || <span style={{ opacity: 0.3 }}>—</span>}
                            </p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleUnlinkContact(p); }}
                            className="text-xs px-2 py-1 rounded-lg hover:opacity-80 transition-all flex-shrink-0"
                            style={{ color: 'var(--text-muted)', border: '1px solid var(--border)', background: 'white' }}>
                            Délier
                          </button>
                        </div>
                      );
                    })}
                  </div>
                    {/* Ghost row — lier un contact existant (hors du scroll horizontal de la liste) */}
                    <div className="pt-1 mt-2">
                      <GhostContactSearch
                        entrepriseId={entPanel.item.entreprise_id}
                        linkedPers={entContacts}
                        onLinked={() => loadEntContacts(entPanel.item.entreprise_id)}
                      />
                    </div>
                    {/* Créer un nouveau contact directement depuis la fiche entreprise */}
                    <button
                      data-testid="btn-create-contact-from-ent"
                      onClick={() => {
                        setDupCheck(null);
                        setPerForm({ ...EMPTY_PER, entreprise_ids: [entPanel.item.entreprise_id], entreprise_id: entPanel.item.entreprise_id });
                        setLinkedEntsMap({ [entPanel.item.entreprise_id]: { entreprise_id: entPanel.item.entreprise_id, nom: entPanel.item.nom, types_entreprise: entPanel.item.types_entreprise || [] } });
                        setOpenPerTagSection(null);
                        setPerAppliedTags([]);
                        setPerPanel({ item: null, _fromEnt: entPanel.item.entreprise_id });
                      }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl transition-all hover:opacity-80 text-sm font-medium mt-2"
                      style={{ border: '1px dashed var(--primary-light)', color: 'var(--primary-light)', background: 'var(--primary-50)' }}>
                      <UserPlus size={14} /> Créer un nouveau contact
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Onglet Comptabilité */}
            {entTab === 'compta' && (
              <div className="flex flex-col">
                {/* ── Comptes Pennylane par société ───────────────────────────────── */}
                {entPanel?.item?.pennylane_comptes?.length > 0 ? (
                  <div className="px-6 py-3 flex items-center gap-4 flex-wrap"
                    style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
                    <span className="text-xs font-semibold uppercase tracking-wide shrink-0"
                      style={{ color: 'var(--text-muted)' }}>Comptes Pennylane</span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {entPanel?.item?.pennylane_comptes.map(c => (
                          <span key={c.societe_id} className="flex items-center gap-1">
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{c.societe_nom}</span>
                            {c.pl_account_customer ? (
                              <code className="text-xs font-mono font-bold px-2 py-0.5 rounded"
                                style={{ background: '#EFF6FF', color: '#1D4ED8', letterSpacing: '0.04em' }}>
                                {c.pl_account_customer}
                              </code>
                            ) : (
                              <code className="text-xs font-mono px-2 py-0.5 rounded"
                                style={{ background: '#F3F4F6', color: '#9CA3AF', letterSpacing: '0.04em' }}>
                                —
                              </code>
                            )}
                          </span>
                        ))}
                    </div>
                    {renderPlLinkButton('customer')}
                  </div>
                ) : (
                  <div className="px-6 py-2 flex items-center gap-2"
                    style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      Comptes Pennylane — non encore assignés (créés automatiquement lors du premier envoi vers Pennylane)
                    </span>
                    {renderPlLinkButton('customer')}
                  </div>
                )}
                {entCompta.loading && (
                  <div className="flex items-center justify-center py-16">
                    <span className="text-sm" style={{ color: 'var(--text-placeholder)' }}>Chargement de la comptabilité…</span>
                  </div>
                )}
                {entCompta.error && (
                  <div className="px-6 py-10 text-center">
                    {entCompta.error.includes('odoo_id') || entCompta.error.includes('non liée') || entCompta.error.includes('non trouvée') ? (
                      <p className="text-sm" style={{ color: 'var(--text-placeholder)' }}>Aucun compte comptable configuré pour cette entreprise.</p>
                    ) : (
                      <p className="text-sm" style={{ color: '#ef4444' }}>{entCompta.error}</p>
                    )}
                  </div>
                )}
                {!entCompta.loading && !entCompta.error && (!entCompta.data?.groups || entCompta.data.groups.length === 0) && (
                  <div className="px-6 py-12 text-center">
                    <p className="text-sm" style={{ color: 'var(--text-placeholder)' }}>Aucune écriture non lettrée pour ce client.</p>
                  </div>
                )}
                {!entCompta.loading && entCompta.data?.groups?.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                    {/* Solde global en haut */}
                    <div className="px-6 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                      <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                        {entCompta.data.groups.reduce((acc, g) => acc + g.lines.length, 0)} écriture{entCompta.data.groups.reduce((acc, g) => acc + g.lines.length, 0) > 1 ? 's' : ''} non lettrée{entCompta.data.groups.reduce((acc, g) => acc + g.lines.length, 0) > 1 ? 's' : ''}
                      </span>
                      <span className="text-sm font-bold" style={{ color: entCompta.data.grand_total > 0 ? '#ef4444' : entCompta.data.grand_total < 0 ? '#059669' : 'var(--text)' }}>
                        Solde total : {formatPrice(Math.abs(entCompta.data.grand_total))} €
                      </span>
                    </div>

                    {/* Un bloc par société */}
                    {entCompta.data.groups.map((group) => (
                      <div key={group.company_id} style={{ marginBottom: 0 }}>
                        {/* En-tête société */}
                        <div className="px-6 py-2 flex items-center justify-between" style={{ background: 'var(--bg-card)', borderBottom: '2px solid var(--border)', borderTop: '1px solid var(--border)' }}>
                          <span className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                            {group.company_nom}
                          </span>
                          <span className="text-xs font-semibold" style={{ color: group.solde_societe > 0 ? '#ef4444' : group.solde_societe < 0 ? '#059669' : 'var(--text)' }}>
                            Sous-total : {formatPrice(Math.abs(group.solde_societe))} €
                          </span>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                              {['Pièce', 'Date', 'Échéance', 'Débit', 'Crédit', 'Solde'].map(h => (
                                <th key={h} style={{
                                  padding: '8px 12px', textAlign: h === 'Pièce' ? 'left' : 'right',
                                  fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap'
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {group.lines.map((line, idx) => (
                              <tr key={line.id} style={{ borderBottom: '1px solid var(--border)', background: idx % 2 === 0 ? 'white' : 'var(--bg-secondary)' }}>
                                <td style={{ padding: '7px 12px', color: 'var(--text)', fontWeight: 500, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    title={line.description || line.piece}>
                                  {line.piece}
                                </td>
                                <td style={{ padding: '7px 12px', textAlign: 'right', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                  {line.date ? (() => { const [y,m,j] = line.date.slice(0,10).split('-'); return `${j}/${m}/${y}`; })() : '—'}
                                </td>
                                <td style={{ padding: '7px 12px', textAlign: 'right', whiteSpace: 'nowrap',
                                  color: line.date_echeance && new Date(line.date_echeance + 'T12:00:00') < new Date() ? '#ef4444' : 'var(--text-muted)' }}>
                                  {line.date_echeance ? (() => { const [y,m,j] = line.date_echeance.slice(0,10).split('-'); return `${j}/${m}/${y}`; })() : '—'}
                                </td>
                                <td style={{ padding: '7px 12px', textAlign: 'right', color: line.debit > 0 ? 'var(--text)' : 'var(--text-placeholder)', fontWeight: line.debit > 0 ? 500 : 400 }}>
                                  {line.debit > 0 ? formatPrice(line.debit) : ''}
                                </td>
                                <td style={{ padding: '7px 12px', textAlign: 'right', color: line.credit > 0 ? 'var(--text)' : 'var(--text-placeholder)', fontWeight: line.credit > 0 ? 500 : 400 }}>
                                  {line.credit > 0 ? formatPrice(line.credit) : ''}
                                </td>
                                <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap',
                                  color: line.solde > 0 ? '#ef4444' : line.solde < 0 ? '#059669' : 'var(--text)' }}>
                                  {formatPrice(Math.abs(line.solde))}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Onglet Compta fournisseur */}
            {entTab === 'compta_fourn' && (
              <div className="flex flex-col">
                {/* Comptes Pennylane fournisseur par société */}
                {entPanel?.item?.pennylane_comptes?.length > 0 ? (
                  <div className="px-6 py-3 flex items-center gap-4 flex-wrap"
                    style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
                    <span className="text-xs font-semibold uppercase tracking-wide shrink-0"
                      style={{ color: 'var(--text-muted)' }}>Comptes Pennylane</span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {entPanel?.item?.pennylane_comptes.map(c => (
                          <span key={c.societe_id} className="flex items-center gap-1">
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{c.societe_nom}</span>
                            {c.pl_account_supplier ? (
                              <code className="text-xs font-mono font-bold px-2 py-0.5 rounded"
                                style={{ background: '#F0FDF4', color: '#15803D', letterSpacing: '0.04em' }}>
                                {c.pl_account_supplier}
                              </code>
                            ) : (
                              <code className="text-xs font-mono px-2 py-0.5 rounded"
                                style={{ background: '#F3F4F6', color: '#9CA3AF', letterSpacing: '0.04em' }}>
                                —
                              </code>
                            )}
                          </span>
                        ))}
                    </div>
                    {renderPlLinkButton('supplier')}
                  </div>
                ) : (
                  <div className="px-6 py-2 flex items-center gap-2"
                    style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      Comptes Pennylane — non encore assignés (créés automatiquement lors du premier envoi vers Pennylane)
                    </span>
                    {renderPlLinkButton('supplier')}
                  </div>
                )}
                {entComptaFourn.loading && (
                  <div className="flex items-center justify-center py-16">
                    <span className="text-sm" style={{ color: 'var(--text-placeholder)' }}>Chargement de la comptabilité…</span>
                  </div>
                )}
                {entComptaFourn.error && (
                  <div className="px-6 py-10 text-center">
                    {entComptaFourn.error.includes('odoo_id') || entComptaFourn.error.includes('non liée') || entComptaFourn.error.includes('non trouvée') ? (
                      <p className="text-sm" style={{ color: 'var(--text-placeholder)' }}>Aucun compte comptable configuré pour ce fournisseur.</p>
                    ) : (
                      <p className="text-sm" style={{ color: '#ef4444' }}>{entComptaFourn.error}</p>
                    )}
                  </div>
                )}
                {!entComptaFourn.loading && !entComptaFourn.error && (!entComptaFourn.data?.groups || entComptaFourn.data.groups.length === 0) && (
                  <div className="px-6 py-12 text-center">
                    <p className="text-sm" style={{ color: 'var(--text-placeholder)' }}>Aucune écriture non lettrée pour ce fournisseur.</p>
                  </div>
                )}
                {!entComptaFourn.loading && entComptaFourn.data?.groups?.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                    <div className="px-6 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                      <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                        {entComptaFourn.data.groups.reduce((acc, g) => acc + g.lines.length, 0)} écriture{entComptaFourn.data.groups.reduce((acc, g) => acc + g.lines.length, 0) > 1 ? 's' : ''} non lettrée{entComptaFourn.data.groups.reduce((acc, g) => acc + g.lines.length, 0) > 1 ? 's' : ''}
                      </span>
                      <span className="text-sm font-bold" style={{ color: entComptaFourn.data.grand_total > 0 ? '#ef4444' : entComptaFourn.data.grand_total < 0 ? '#059669' : 'var(--text)' }}>
                        Solde total : {formatPrice(Math.abs(entComptaFourn.data.grand_total))} €
                      </span>
                    </div>
                    {entComptaFourn.data.groups.map((group) => (
                      <div key={group.company_id} style={{ marginBottom: 0 }}>
                        <div className="px-6 py-2 flex items-center justify-between" style={{ background: 'var(--bg-card)', borderBottom: '2px solid var(--border)', borderTop: '1px solid var(--border)' }}>
                          <span className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                            {group.company_nom}
                          </span>
                          <span className="text-xs font-semibold" style={{ color: group.solde_societe > 0 ? '#ef4444' : group.solde_societe < 0 ? '#059669' : 'var(--text)' }}>
                            Sous-total : {formatPrice(Math.abs(group.solde_societe))} €
                          </span>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                              {['Pièce', 'Date', 'Échéance', 'Débit', 'Crédit', 'Solde'].map(h => (
                                <th key={h} style={{
                                  padding: '8px 12px', textAlign: h === 'Pièce' ? 'left' : 'right',
                                  fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap'
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {group.lines.map((line, idx) => (
                              <tr key={line.id} style={{ borderBottom: '1px solid var(--border)', background: idx % 2 === 0 ? 'white' : 'var(--bg-secondary)' }}>
                                <td style={{ padding: '7px 12px', color: 'var(--text)', fontWeight: 500, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    title={line.description || line.piece}>
                                  {line.piece}
                                </td>
                                <td style={{ padding: '7px 12px', textAlign: 'right', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                  {line.date ? (() => { const [y,m,j] = line.date.slice(0,10).split('-'); return `${j}/${m}/${y}`; })() : '—'}
                                </td>
                                <td style={{ padding: '7px 12px', textAlign: 'right', whiteSpace: 'nowrap',
                                  color: line.date_echeance && new Date(line.date_echeance + 'T12:00:00') < new Date() ? '#ef4444' : 'var(--text-muted)' }}>
                                  {line.date_echeance ? (() => { const [y,m,j] = line.date_echeance.slice(0,10).split('-'); return `${j}/${m}/${y}`; })() : '—'}
                                </td>
                                <td style={{ padding: '7px 12px', textAlign: 'right', color: line.debit > 0 ? 'var(--text)' : 'var(--text-placeholder)', fontWeight: line.debit > 0 ? 500 : 400 }}>
                                  {line.debit > 0 ? formatPrice(line.debit) : ''}
                                </td>
                                <td style={{ padding: '7px 12px', textAlign: 'right', color: line.credit > 0 ? 'var(--text)' : 'var(--text-placeholder)', fontWeight: line.credit > 0 ? 500 : 400 }}>
                                  {line.credit > 0 ? formatPrice(line.credit) : ''}
                                </td>
                                <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap',
                                  color: line.solde > 0 ? '#ef4444' : line.solde < 0 ? '#059669' : 'var(--text)' }}>
                                  {formatPrice(Math.abs(line.solde))}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Onglet Conditions commerciales ──────────────────────── */}
            {entTab === 'conditions' && entPanel?.item && (
              <ConditionsTab
                entreprise={entPanel.item}
                agences={agences}
                saveRef={condSaveRef}
                onSave={async () => {
                  const res = await api.getEntreprise(entPanel.item.entreprise_id);
                  setEntPanel(p => ({ ...p, item: res.data }));
                  // Mettre à jour immédiatement l'entrée dans la liste (évite le stale cache)
                  setEntreprises(prev => prev.map(e =>
                    e.entreprise_id === res.data.entreprise_id ? { ...e, ...res.data } : e
                  ));
                }}
              />
            )}
            {entTab === 'discussions' && entPanel?.item && (
              <div style={{ padding: '16px 0' }}>
                <ActivitesFichePanel
                  record_type="entreprise"
                  record_id={entPanel.item.entreprise_id}
                  record_label={entPanel.item.nom}
                  type_document="entreprise"
                  numero_document={entPanel.item.nom}
                />
              </div>
            )}

            {/* ── Onglet Matériels ──────────────────────────────────────────── */}
            {entTab === 'materiels' && entPanel?.item && (
              <div className="px-4 sm:px-6 py-5 space-y-5">

                {/* Rapport annuel */}
                <div className="p-4 rounded-xl border"
                  style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Wrench size={14} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />
                    <span className="text-sm font-semibold whitespace-nowrap" style={{ color: 'var(--text)' }}>
                      Rapport technique annuel
                    </span>

                    {/* Toggle mode */}
                    <div className="flex rounded-lg overflow-hidden border text-xs font-medium"
                      style={{ borderColor: 'var(--border)' }}>
                      {[
                        { value: 'proprietaire', label: 'Propriétaire → Exploitant' },
                        { value: 'exploitant',   label: 'Exploitant → Propriétaire' },
                      ].map(opt => (
                        <button key={opt.value}
                          data-testid={`rapport-mode-${opt.value}`}
                          onClick={() => setRapportMode(opt.value)}
                          className="px-3 py-1.5 transition-colors whitespace-nowrap"
                          style={{
                            background: rapportMode === opt.value ? 'var(--primary-solid)' : 'white',
                            color:      rapportMode === opt.value ? 'white' : 'var(--text-muted)',
                          }}>
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {/* Année */}
                    <select
                      value={rapportAnnee}
                      onChange={e => setRapportAnnee(Number(e.target.value))}
                      data-testid="rapport-annee-select"
                      className="text-sm rounded-lg border px-2 py-1.5"
                      style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)', minWidth: 80 }}>
                      {anneesCourantes.map(a => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>

                    {/* Télécharger */}
                    <button
                      onClick={() => handleTelechargerRapport(entPanel.item.entreprise_id)}
                      disabled={rapportLoading}
                      data-testid="btn-telecharger-rapport"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ml-auto"
                      style={{ background: 'var(--primary-solid)', color: 'white', opacity: rapportLoading ? .6 : 1 }}>
                      {rapportLoading
                        ? <Loader2 size={14} className="animate-spin" />
                        : <Download size={14} />}
                      Télécharger PDF
                    </button>
                  </div>
                </div>

                {/* Liste matériels */}
                {entMateriels.loading && (
                  <div className="flex items-center gap-2 py-6 justify-center text-sm" style={{ color: 'var(--text-muted)' }}>
                    <Loader2 size={16} className="animate-spin" /> Chargement...
                  </div>
                )}
                {!entMateriels.loading && entMateriels.data.length === 0 && (
                  <div className="py-8 text-center text-sm" style={{ color: 'var(--text-placeholder)' }}>
                    Aucun matériel lié à cette entreprise.
                  </div>
                )}
                {!entMateriels.loading && entMateriels.data.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>
                      {entMateriels.data.length} matériel{entMateriels.data.length > 1 ? 's' : ''} lié{entMateriels.data.length > 1 ? 's' : ''}
                    </p>
                    <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                      <table className="w-full text-xs">
                        <thead>
                          <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                            <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Nom</th>
                            <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Marque / Modèle</th>
                            <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>N° Parc</th>
                            <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Immatriculation</th>
                            <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Rôle</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entMateriels.data.map((m, i) => (
                            <tr key={m.materiel_id}
                              style={{
                                borderBottom: i < entMateriels.data.length - 1 ? '1px solid var(--border-light)' : 'none',
                                background: i % 2 === 0 ? 'white' : 'var(--bg-subtle)',
                              }}>
                              <td className="px-3 py-2 font-medium" style={{ color: 'var(--text)' }}>{m.nom || '—'}</td>
                              <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>
                                {[m.marque, m.modele].filter(Boolean).join(' ') || '—'}
                              </td>
                              <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>{m.numero_parc || '—'}</td>
                              <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>{m.immatriculation || '—'}</td>
                              <td className="px-3 py-2">
                                <span className="px-1.5 py-0.5 rounded text-xs font-medium"
                                  style={{
                                    background: m.proprietaire_id === entPanel.item.entreprise_id ? '#F0FDF4' : '#EFF6FF',
                                    color:      m.proprietaire_id === entPanel.item.entreprise_id ? '#15803D' : '#1D4ED8',
                                  }}>
                                  {m.proprietaire_id === entPanel.item.entreprise_id ? 'Propriétaire' : 'Exploitant'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
            {entTab === 'conditions' && !entPanel?.item && (
              <div className="px-6 py-10 text-center text-sm" style={{ color: 'var(--text-placeholder)' }}>
                Enregistrez d'abord l'entreprise pour gérer les conditions commerciales.
              </div>
            )}

            {/* ── Appétences ─────────────────────────────────────────────── */}
            {entTab === 'appetences' && entPanel?.item && (
              <div className="px-4 sm:px-6 py-5 space-y-4">

                <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>

                  {/* En-tête card */}
                  <div className="flex items-center gap-2 px-4 py-3 border-b"
                    style={{ background: 'var(--bg-subtle)', borderColor: 'var(--border)' }}>
                    <TrendingUp size={14} style={{ color: 'var(--primary-solid)' }} />
                    <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
                      Appétences Location
                    </span>
                    <span className="text-xs ml-1" style={{ color: 'var(--text-muted)' }}>
                      — 12 derniers mois
                    </span>
                    <button
                      onClick={() => loadEntAppetences(entPanel.item.entreprise_id)}
                      className="ml-auto p-1 rounded hover:bg-white/60 transition-colors"
                      title="Rafraîchir"
                      data-testid="btn-refresh-appetences">
                      <RefreshCw size={13} style={{ color: 'var(--text-muted)' }} />
                    </button>
                  </div>

                  {entAppetences.loading && (
                    <div className="flex items-center gap-2 py-8 justify-center text-sm" style={{ color: 'var(--text-muted)' }}>
                      <Loader2 size={16} className="animate-spin" /> Chargement...
                    </div>
                  )}
                  {!entAppetences.loading && entAppetences.data.length === 0 && !entAppetences.error && (
                    <div className="py-10 text-center text-sm" style={{ color: 'var(--text-placeholder)' }}>
                      Aucune location sur les 12 derniers mois.
                    </div>
                  )}
                  {entAppetences.error && (
                    <div className="py-6 text-center text-sm" style={{ color: '#EF4444' }}>
                      {entAppetences.error}
                    </div>
                  )}

                  {!entAppetences.loading && entAppetences.data.length > 0 && (
                    <table className="w-full text-xs" style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 28 }} />  {/* expand toggle */}
                        <col />                          {/* catégorie / gamme */}
                        <col style={{ width: 68 }} />  {/* contrats */}
                        <col style={{ width: 68 }} />  {/* jours */}
                        <col style={{ width: 88 }} />  {/* CA HT */}
                        <col style={{ width: 84 }} />  {/* dernière loc */}
                      </colgroup>
                      <thead>
                        <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                          <th />
                          <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Catégorie / Gamme</th>
                          <th className="px-2 py-2 text-center font-semibold" style={{ color: 'var(--text-muted)' }}>Contrats</th>
                          <th className="px-2 py-2 text-center font-semibold" style={{ color: 'var(--text-muted)' }}>Jours</th>
                          <th className="px-2 py-2 text-right font-semibold" style={{ color: 'var(--text-muted)' }}>CA HT</th>
                          <th className="px-2 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Dernière loc.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entAppetences.data.map((cat, i) => {
                          const key = cat.categorie_nom;
                          const isExpanded = appetencesExpanded.has(key);
                          const toggle = () => setAppetencesExpanded(prev => {
                            const next = new Set(prev);
                            next.has(key) ? next.delete(key) : next.add(key);
                            return next;
                          });
                          const hasGammes = (cat.gammes || []).length > 0;
                          return (
                            <React.Fragment key={key}>
                              {/* Ligne catégorie */}
                              <tr
                                onClick={hasGammes ? toggle : undefined}
                                style={{
                                  borderBottom: '1px solid var(--border-light)',
                                  background: isExpanded ? '#EEF3FF' : i % 2 === 0 ? 'white' : 'var(--bg-subtle)',
                                  cursor: hasGammes ? 'pointer' : 'default',
                                }}
                                className={hasGammes ? 'hover:opacity-90 transition-opacity' : ''}>
                                <td className="pl-2 py-2.5 text-center">
                                  {hasGammes && (isExpanded
                                    ? <ChevronDown size={13} style={{ color: 'var(--primary-solid)' }} />
                                    : <ChevronRight size={13} style={{ color: 'var(--text-muted)' }} />
                                  )}
                                </td>
                                <td className="px-3 py-2.5 font-bold" style={{ color: 'var(--text)' }}>
                                  {cat.categorie_nom || '—'}
                                  {hasGammes && (
                                    <span className="ml-2 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>
                                      {cat.gammes.length} gamme{cat.gammes.length > 1 ? 's' : ''}
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-2.5 text-center tabular-nums font-bold" style={{ color: 'var(--primary-solid)' }}>
                                  {cat.nb_contrats}
                                </td>
                                <td className="px-2 py-2.5 text-center tabular-nums" style={{ color: 'var(--text-muted)' }}>
                                  {cat.nb_jours_total}
                                </td>
                                <td className="px-2 py-2.5 text-right tabular-nums font-bold" style={{ color: 'var(--text)' }}>
                                  {cat.ca_total > 0 ? `${cat.ca_total.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €` : '—'}
                                </td>
                                <td className="px-2 py-2.5 tabular-nums" style={{ color: 'var(--text-muted)' }}>
                                  {cat.date_derniere ? new Date(cat.date_derniere).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                                </td>
                              </tr>

                              {/* Lignes gammes (dépliées) */}
                              {isExpanded && (cat.gammes || []).map((g, gi) => (
                                <tr key={g.gamme_id || g.gamme_nom}
                                  style={{
                                    borderBottom: gi < cat.gammes.length - 1 ? '1px solid #DBEAFE' : '1px solid var(--border)',
                                    background: gi % 2 === 0 ? '#F8FAFF' : 'white',
                                    borderLeft: '3px solid var(--primary-solid)',
                                  }}>
                                  <td />
                                  <td className="py-2 font-medium" style={{ paddingLeft: 28, color: 'var(--text)' }}>
                                    {g.gamme_nom || '—'}
                                  </td>
                                  <td className="px-2 py-2 text-center tabular-nums font-semibold" style={{ color: 'var(--primary-solid)', opacity: 0.8 }}>
                                    {g.nb_contrats}
                                  </td>
                                  <td className="px-2 py-2 text-center tabular-nums" style={{ color: 'var(--text-muted)' }}>
                                    {g.nb_jours}
                                  </td>
                                  <td className="px-2 py-2 text-right tabular-nums" style={{ color: 'var(--text-muted)' }}>
                                    {g.ca_total > 0 ? `${g.ca_total.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €` : '—'}
                                  </td>
                                  <td />
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {!entAppetences.loading && entAppetences.data.length > 0 && (
                  <p className="text-xs" style={{ color: 'var(--text-placeholder)' }}>
                    <BarChart2 size={11} className="inline mr-1" />
                    {entAppetences.data.length} catégorie{entAppetences.data.length > 1 ? 's' : ''} — contrats Location des 12 derniers mois (hors annulés).
                  </p>
                )}
              </div>
            )}

            {entTab === 'documents' && entPanel?.item && (
              <div className="p-4">
                <DocumentsTab
                  entityId={entPanel.item.entreprise_id}
                  listFn={() => api.getEntrepriseDocuments(entPanel.item.entreprise_id)}
                  uploadFn={(formData) => api.uploadEntrepriseDocument(entPanel.item.entreprise_id, formData)}
                  updateFn={(docId, data) => api.patchEntrepriseDocument(entPanel.item.entreprise_id, docId, data)}
                  deleteFn={(docId) => api.deleteEntrepriseDocument(entPanel.item.entreprise_id, docId)}
                  downloadFn={(docId) => api.downloadEntrepriseDocument(entPanel.item.entreprise_id, docId)}
                  onCountChange={setEntDocCount}
                />
              </div>
            )}

            {/* ── Fin des onglets ─────────────────────────────────────────── */}
          </div>

          {/* ── Footer ───────────────────────────────────────────────────── */}
          <div className="flex-none px-6 py-4 flex items-center justify-between"
               style={{ borderTop: '1px solid var(--border)', background: 'white' }}>
            {/* Bouton Archiver — déplacé ici depuis le header */}
            {canArchive && entPanel?.item ? (
              <button
                data-testid="btn-toggle-archive"
                onClick={handleToggleArchive}
                title={entPanel.item.archive ? 'Désarchiver ce contact' : 'Archiver ce contact (masqué dans les listes)'}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-80"
                style={{
                  color: entPanel.item.archive ? '#92400E' : 'var(--text-muted)',
                  border: `1px solid ${entPanel.item.archive ? '#FDE68A' : 'var(--border)'}`,
                  background: entPanel.item.archive ? '#FEF3C7' : 'white',
                }}>
                <Archive size={14} />
                {entPanel.item.archive ? 'Désarchiver' : 'Archiver'}
              </button>
            ) : <div />}
            <div className="flex gap-2">
              {entPanel?.item && (
                <div className="relative" ref={printMenuRef}>
                  <button
                    data-testid="btn-imprimer-fiche"
                    onClick={() => setPrintMenuOpen(o => !o)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-80"
                    style={{ color: 'var(--text-muted)', border: '1px solid var(--border)', background: 'white' }}
                    title="Imprimer">
                    <Printer size={14} /> Imprimer <ChevronDown size={12} />
                  </button>
                  {printMenuOpen && (
                    <div className="absolute bottom-full mb-1 left-0 z-50 rounded-lg shadow-lg border overflow-hidden"
                         style={{ background: 'white', borderColor: 'var(--border)', minWidth: '200px' }}>
                      <button
                        data-testid="btn-imprimer-fiche-option"
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2"
                        style={{ color: 'var(--text)' }}
                        onClick={async () => {
                          setPrintMenuOpen(false);
                          try {
                            const res = await api.getEntreprisePdf(entPanel.item.entreprise_id);
                            const blob = new Blob([res.data], { type: 'application/pdf' });
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                          } catch { toast.error('Erreur lors de la génération du PDF'); }
                        }}>
                        <FileText size={14} style={{ color: 'var(--text-muted)' }} /> Fiche entreprise
                      </button>
                      <button
                        data-testid="btn-imprimer-releve"
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap"
                        style={{ color: 'var(--text)', borderTop: '1px solid var(--border)' }}
                        onClick={async () => {
                          setPrintMenuOpen(false);
                          try {
                            const token = localStorage.getItem('token');
                            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/entreprises/${entPanel.item.entreprise_id}/releve-compte`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            if (!resp.ok) { const e = await resp.json().catch(() => ({})); toast.error(e.detail || 'Erreur'); return; }
                            const ab = await resp.arrayBuffer();
                            const blob = new Blob([ab], { type: 'application/pdf' });
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                            // Ne pas révoquer l'URL trop tôt — Chrome ne peut plus télécharger si l'URL est révoquée
                            // La mémoire est libérée automatiquement à la fermeture de l'onglet
                          } catch (e) {
                            const msg = e?.response?.status === 400 ? 'Aucune facture trouvée pour ce client' : 'Erreur lors de la génération du relevé';
                            toast.error(msg);
                          }
                        }}>
                        <FileText size={14} style={{ color: 'var(--text-muted)' }} /> Relevé de compte (PDF)
                      </button>
                      <button
                        data-testid="btn-imprimer-releve-mail"
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap"
                        style={{ color: 'var(--text)', borderTop: '1px solid var(--border)' }}
                        onClick={async () => {
                          setPrintMenuOpen(false);
                          releveEntrepriseRef.current = entPanel.item;
                          setEntPanel(null); // fermer le entPanel pour libérer le focus trap Radix
                          setReleveMailLoading(true);
                          setShowReleveMail(true);
                          try {
                            const resp = await api.getRelevePdfTemplateMail(releveEntrepriseRef.current.entreprise_id);
                            setReleveMail({ sujet: resp.data.sujet, corps: resp.data.corps, destinataires: [] });
                          } catch { toast.error('Erreur lors du chargement du modèle email'); setShowReleveMail(false); }
                          finally { setReleveMailLoading(false); }
                        }}>
                        <Mail size={14} style={{ color: '#7C3AED' }} /> Relevé de compte par mail
                      </button>
                    </div>
                  )}
                </div>
              )}
              <button onClick={() => setEntPanel(null)} className="px-4 py-2 rounded-lg text-sm border transition-all hover:opacity-80"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                {entTab === 'contacts' ? 'Fermer' : 'Annuler'}
              </button>
              {entTab !== 'contacts' && (
                <button
                  onClick={() => entTab === 'conditions' ? condSaveRef.current?.() : saveEnt()}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                  style={{ background: 'var(--primary-gradient)' }} data-testid="save-ent-btn">
                  {saving ? '…' : (entPanel?.item ? 'Enregistrer' : 'Créer')}
                </button>
              )}
            </div>
          </div>

        </div>
      </SidePanel>

      {/* ═══ PANNEAU CONTACT ══════════════════════════════════════════════ */}
      <SidePanel open={!!perPanel} onOpenChange={(v) => { if (!v) setPerPanel(null); }}
        title={perPanel?.item ? `${[perPanel.item.prenom, perPanel.item.nom].filter(Boolean).join(' ')}` : (perPanel?._isParticulier ? 'Nouveau particulier' : 'Nouveau contact')}
        copyValue={perPanel?.item ? [perPanel.item.prenom, perPanel.item.nom].filter(Boolean).join(' ') : undefined}
        width={648}
        footer={
          <div className="flex items-center justify-between w-full">
            {perPanel?.item ? (
              <div className="flex items-center gap-2">
                <button onClick={() => deletePer(perPanel.item.personne_id)} disabled={deleting || promotingPer}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-80 disabled:opacity-50"
                  style={{ color: '#ef4444', border: '1px solid #fecaca', background: 'white' }}>
                  <Trash2 size={14} /> Supprimer
                </button>
                <button onClick={handlePromotePer} disabled={deleting || promotingPer}
                  data-testid="promote-to-entreprise-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-80 disabled:opacity-50 whitespace-nowrap"
                  style={{ color: '#D97706', border: '1px solid #FDE68A', background: '#FFFBEB' }}
                  title="Convertir ce contact en fiche entreprise">
                  <Building2 size={14} /> {promotingPer ? 'Promotion…' : 'Promouvoir en entreprise'}
                </button>
              </div>
            ) : <div />}
            <div className="flex gap-2">
              {/* Bouton Sync Pennylane — pour toutes les personnes */}
              {perPanel?.item && plDossiers.length > 0 && (
                <div className="relative" ref={plMenuPerRef}>
                  {plDossiers.length === 1 ? (
                    /* Un seul dossier : bouton direct */
                    <button
                      data-testid="btn-sync-pl-particulier"
                      disabled={plSyncingPer}
                      onClick={async () => {
                        setPlSyncingPer(true);
                        try {
                          const res = await api.syncParticulierToPL(perPanel.item.personne_id, plDossiers[0].societe_id);
                          const compte = res.data?.results?.[0]?.pl_account_customer;
                          if (res.data?.synced > 0) {
                            toast.success(`Synchronisé Pennylane${compte ? ` · compte ${compte}` : ''}`);
                          } else {
                            toast.error(res.data?.results?.[0]?.error || 'Erreur synchronisation Pennylane');
                          }
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Erreur Pennylane');
                        } finally { setPlSyncingPer(false); }
                      }}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-80 disabled:opacity-50"
                      style={{ color: '#7C3AED', border: '1px solid #DDD6FE', background: '#FAF5FF' }}>
                      {plSyncingPer
                        ? <span className="w-3.5 h-3.5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                        : <Scale size={13} />}
                      {plSyncingPer ? '…' : 'Lier à PL'}
                    </button>
                  ) : (
                    /* Plusieurs dossiers : dropdown */
                    <>
                      <button
                        data-testid="btn-sync-pl-particulier"
                        disabled={plSyncingPer}
                        onClick={() => setPlMenuPerOpen(o => !o)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-80 disabled:opacity-50"
                        style={{ color: '#7C3AED', border: '1px solid #DDD6FE', background: '#FAF5FF' }}>
                        {plSyncingPer ? '…' : <><Scale size={13} /> Lier à PL <ChevronDown size={12} /></>}
                      </button>
                      {plMenuPerOpen && (
                        <div className="absolute bottom-full mb-1 right-0 z-50 rounded-lg shadow-lg border overflow-hidden"
                             style={{ background: 'white', borderColor: 'var(--border)', minWidth: '200px' }}>
                          {plDossiers.map(d => (
                            <button
                              key={d.societe_id}
                              data-testid={`pl-per-dossier-${d.societe_id}`}
                              className="w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors border-b last:border-b-0 text-sm"
                              style={{ borderColor: '#EDE9FE' }}
                              onClick={async () => {
                                setPlMenuPerOpen(false);
                                setPlSyncingPer(true);
                                try {
                                  const res = await api.syncParticulierToPL(perPanel.item.personne_id, d.societe_id);
                                  const compte = res.data?.results?.[0]?.pl_account_customer;
                                  if (res.data?.synced > 0) {
                                    toast.success(`Synchronisé Pennylane${compte ? ` · compte ${compte}` : ''}`);
                                  } else {
                                    toast.error(res.data?.results?.[0]?.error || 'Erreur synchronisation Pennylane');
                                  }
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || 'Erreur Pennylane');
                                } finally { setPlSyncingPer(false); }
                              }}>
                              <div className="font-medium" style={{ color: '#5B21B6' }}>{d.nom}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              <button onClick={savePer} disabled={saving || (!perPanel?.item && dupCheck?.blocked)}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'var(--primary-gradient)' }} data-testid="save-per-btn">
                {saving ? '…' : (perPanel?.item ? 'Enregistrer' : 'Créer')}
              </button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {/* ── Alerte doublons (création uniquement) ─────────────────────── */}
          {!perPanel?.item && dupCheck?.blocked && (
            <div data-testid="dup-check-blocked"
              className="flex items-start gap-3 px-4 py-3 rounded-xl"
              style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
              <AlertCircle size={16} style={{ color: '#DC2626', flexShrink: 0, marginTop: 2 }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: '#991B1B' }}>
                  {dupCheck.blocking_reason === 'email'
                    ? 'Un contact avec cet e-mail existe déjà'
                    : 'Un contact avec ce téléphone portable existe déjà'}
                </p>
                {dupCheck.blocking_matches?.map(m => {
                  const fullName = [m.prenom, m.nom].filter(Boolean).join(' ');
                  return (
                    <p key={m.personne_id} className="text-xs mt-1" style={{ color: '#DC2626' }}>
                      {fullName}{m.email ? ` — ${m.email}` : ''}{m.telephone_mobile ? ` — ${m.telephone_mobile}` : ''}
                    </p>
                  );
                })}
                <p className="text-xs mt-1.5" style={{ color: '#7F1D1D' }}>
                  Modifiez ou supprimez le champ en conflit pour continuer.
                </p>
              </div>
            </div>
          )}
          {!perPanel?.item && !dupCheck?.blocked && dupCheck?.name_warning && (
            <div data-testid="dup-check-warning"
              className="flex items-start gap-3 px-4 py-3 rounded-xl"
              style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
              <AlertCircle size={16} style={{ color: '#D97706', flexShrink: 0, marginTop: 2 }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: '#92400E' }}>
                  Un contact avec ce nom existe déjà
                </p>
                {dupCheck.name_matches?.map(m => {
                  const fullName = [m.prenom, m.nom].filter(Boolean).join(' ');
                  return (
                    <p key={m.personne_id} className="text-xs mt-1" style={{ color: '#D97706' }}>
                      {fullName}{m.email ? ` — ${m.email}` : ''}{m.telephone_mobile ? ` — ${m.telephone_mobile}` : ''}
                    </p>
                  );
                })}
                <p className="text-xs mt-1.5" style={{ color: '#78350F' }}>
                  Vous pouvez tout de même créer ce contact s'il s'agit d'une personne différente.
                </p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl} style={lblSt}>Prénom</label>
              <input value={perForm.prenom} onChange={e => setPerForm(f => ({ ...f, prenom: e.target.value }))}
                className={inp} style={inpSt} />
            </div>
            <div>
              <label className={lbl} style={lblSt}>Nom *</label>
              <input value={perForm.nom} onChange={e => setPerForm(f => ({ ...f, nom: e.target.value }))}
                className={inp} style={inpSt} data-testid="per-nom-input" />
            </div>
          </div>
          {/* Téléphones */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl} style={lblSt}>Téléphone mobile</label>
              <PhoneInput value={perForm.telephone_mobile ?? ''} onChange={e => setPerForm(f => ({ ...f, telephone_mobile: e.target.value }))}
                className={inp} style={inpSt} />
            </div>
            <div>
              <label className={lbl} style={lblSt}>Ligne directe</label>
              <PhoneInput value={perForm.telephone_fixe ?? ''} onChange={e => setPerForm(f => ({ ...f, telephone_fixe: e.target.value }))}
                className={inp} style={inpSt} />
            </div>
          </div>
          <div>
            <label className={lbl} style={lblSt}>Email</label>
            <input value={perForm.email} onChange={e => setPerForm(f => ({ ...f, email: e.target.value }))}
              type="email" className={inp} style={inpSt} />
          </div>
          {/* Champs adresse — toujours visibles (utiles pour les particuliers B2C) */}
          <div>
            <label className={lbl} style={lblSt}>Adresse</label>
            <input value={perForm.adresse} onChange={e => setPerForm(f => ({ ...f, adresse: e.target.value }))}
              className={inp} style={inpSt} placeholder="N° et libellé de voie" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={lbl} style={lblSt}>Code postal</label>
              <input value={perForm.code_postal} onChange={e => setPerForm(f => ({ ...f, code_postal: e.target.value }))}
                className={inp} style={inpSt} placeholder="97100" />
            </div>
            <div>
              <label className={lbl} style={lblSt}>Ville</label>
              <input value={perForm.ville} onChange={e => setPerForm(f => ({ ...f, ville: e.target.value }))}
                className={inp} style={inpSt} />
            </div>
            <div>
              <label className={lbl} style={lblSt}>Nationalité</label>
              <PaysSelect value={perForm.pays_code || 'FR'} onChange={code => setPerForm(f => ({ ...f, pays_code: code }))}
                className={inp} style={inpSt} testid="personne-nationalite-select" />
            </div>
          </div>
          {/* ── Tags (fiche existante uniquement) ─────────────────────── */}
          {perPanel?.item && (() => {
            const perCatalog = allCatalogTags.filter(t => t.pour_personne && t.can_use !== false);
            const appliedIds = new Set(perAppliedTags.map(t => t.tag_id));
            const capitalize = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

            const SECTIONS = [
              { key: 'fonction',      label: 'Fonctions' },
              { key: 'automation',    label: 'Automatisations' },
              { key: 'qualification', label: 'Qualifications' },
            ];

            // Tags client (tiers_tags) : B2C = éditable sur le tiers de la personne ;
            // B2B = rappel lecture seule du statut du tiers de l'entreprise rattachée
            const isB2C = perLinkedEnts.length === 0;
            const tiersCatalog = allCatalogTags.filter(t => t.pour_tiers && !['fonction', 'automation'].includes(t.tag_type) && t.can_use !== false);
            const tiersAppliedIds = new Set(perTiersTags.map(t => t.tag_id));
            const isTiersOpen = openPerTagSection === 'tiers_client';

            return (
              <div className="flex flex-col gap-3" ref={perTagMenuRef}>
                {/* ── Tags Client / Solvabilité (tiers_tags) ── */}
                <div data-testid="per-tiers-tags-section">
                  <label className={lbl} style={lblSt}>
                    Tags Client / Solvabilité
                    {!isB2C && perLinkedEnts[0] && (
                      <span className="ml-1 font-normal" style={{ color: 'var(--text-muted)' }}>
                        — {perLinkedEnts[0].nom} (lecture seule)
                      </span>
                    )}
                  </label>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    {perTiersTags.length === 0 && (
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Aucun tag client</span>
                    )}
                    {perTiersTags.map(t => (
                      <span key={t.tag_id}
                        className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium"
                        style={{ background: t.couleur + '22', color: t.couleur, border: `1px solid ${t.couleur}55` }}
                        data-testid={`per-tiers-tag-${t.tag_id}`}>
                        {capitalize(t.nom)}
                        {isB2C && (
                          <button onClick={() => handleRemovePerTiersTag(t.tag_id)}
                            className="ml-0.5 hover:opacity-70 transition-opacity leading-none"
                            data-testid={`per-tiers-tag-remove-${t.tag_id}`}>×</button>
                        )}
                      </span>
                    ))}
                    {isB2C && perPanel?.item?.tiers_id && (
                      <div className="relative">
                        <button
                          data-testid="btn-add-per-tiers-tag"
                          onClick={() => setOpenPerTagSection(v => v === 'tiers_client' ? null : 'tiers_client')}
                          className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-all"
                          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'white' }}>
                          <Tag size={10} />
                          Ajouter
                          <ChevronDown size={9} style={{ transition: 'transform .15s', transform: isTiersOpen ? 'rotate(180deg)' : 'none' }} />
                        </button>
                        {isTiersOpen && (
                          <div className="absolute z-50 mt-1 rounded-xl shadow-lg border bg-white py-1 w-56"
                            style={{ borderColor: 'var(--border)', top: '100%', left: 0, maxHeight: '220px', overflowY: 'auto' }}>
                            {tiersCatalog.length === 0 ? (
                              <div className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                                Aucun tag — créez-en dans Paramètres
                              </div>
                            ) : tiersCatalog.map(t => {
                              const isApplied = tiersAppliedIds.has(t.tag_id);
                              return (
                                <label key={t.tag_id}
                                  className="flex items-center gap-2.5 px-3 py-1.5 cursor-pointer hover:bg-gray-50 text-xs"
                                  style={{ color: 'var(--text)' }}>
                                  <input type="checkbox" className="w-3.5 h-3.5 accent-indigo-600"
                                    checked={isApplied}
                                    onChange={() => isApplied ? handleRemovePerTiersTag(t.tag_id) : handleApplyPerTiersTag(t.tag_id)}
                                    data-testid={`per-tiers-tag-checkbox-${t.tag_id}`} />
                                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t.couleur }} />
                                  <span className="flex-1">{capitalize(t.nom)}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* ── Tags Contact / Rôle (personne_tags) ── */}
                {SECTIONS.map(({ key, label }) => {
                  const chips = perAppliedTags.filter(t => t.tag_type === key);
                  const available = perCatalog.filter(t => t.tag_type === key);
                  const isOpen = openPerTagSection === key;
                  return (
                    <div key={key}>
                      <label className={lbl} style={lblSt}>{label}</label>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {chips.map(t => (
                          <span key={t.tag_id}
                            className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium"
                            style={{ background: t.couleur + '22', color: t.couleur, border: `1px solid ${t.couleur}55` }}>
                            {capitalize(t.nom)}
                            <button onClick={() => handleRemovePerTag(t.tag_id)}
                              className="ml-0.5 hover:opacity-70 transition-opacity leading-none"
                              data-testid={`per-tag-remove-${t.tag_id}`}>×</button>
                          </span>
                        ))}
                        {/* Dropdown par section */}
                        <div className="relative">
                          <button
                            data-testid={`btn-add-per-tag-${key}`}
                            onClick={() => setOpenPerTagSection(v => v === key ? null : key)}
                            className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-all"
                            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'white' }}>
                            <Tag size={10} />
                            Ajouter
                            <ChevronDown size={9} style={{ transition: 'transform .15s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                          </button>
                          {isOpen && (
                            <div className="absolute z-50 mt-1 rounded-xl shadow-lg border bg-white py-1 w-56"
                              style={{ borderColor: 'var(--border)', top: '100%', left: 0, maxHeight: '220px', overflowY: 'auto' }}>
                              {available.length === 0 ? (
                                <div className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                                  Aucun tag — créez-en dans Paramètres
                                </div>
                              ) : available.map(t => {
                                const isApplied = appliedIds.has(t.tag_id);
                                return (
                                  <label key={t.tag_id}
                                    className="flex items-center gap-2.5 px-3 py-1.5 cursor-pointer hover:bg-gray-50 text-xs"
                                    style={{ color: 'var(--text)' }}>
                                    <input type="checkbox" className="w-3.5 h-3.5 accent-indigo-600"
                                      checked={isApplied}
                                      onChange={() => isApplied ? handleRemovePerTag(t.tag_id) : handleApplyPerTag(t.tag_id)}
                                      data-testid={`per-tag-checkbox-${t.tag_id}`} />
                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t.couleur }} />
                                    <span className="flex-1">{capitalize(t.nom)}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Entreprises liées — ghost row */}
          <div>
            <label className={lbl} style={lblSt}>Entreprise(s)</label>
            <div className="space-y-2">
              {perLinkedEnts.map(ent => (
                <div key={ent.entreprise_id} className="flex items-center gap-2 px-3 py-2 rounded-xl"
                     style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
                  <Building2 size={13} style={{ color: 'var(--primary-light)', flexShrink: 0 }} />
                  <span className="text-sm font-medium flex-1 truncate" style={{ color: 'var(--text)' }}>{ent.nom}</span>
                  {(ent.types_entreprise || []).slice(0, 1).map(t => <TypeBadge key={t} type={t} />)}
                  <button onClick={() => handleRemovePerEnt(ent.entreprise_id)}
                          className="text-xs px-2 py-1 rounded-lg hover:opacity-80 transition-all flex-shrink-0"
                          style={{ color: 'var(--text-muted)', border: '1px solid var(--border)', background: 'white' }}>
                    Délier
                  </button>
                </div>
              ))}
              <GhostEntrepriseSearch
                linkedEntIds={perForm.entreprise_ids || []}
                onAdd={handleAddPerEnt}
              />
            </div>
          </div>
          <div>
            <label className={lbl} style={lblSt}>Notes</label>
            <textarea value={perForm.notes} onChange={e => { setPerForm(f => ({ ...f, notes: e.target.value })); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
              onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
              ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
              rows={2} className={inp} style={{ ...inpSt, resize: 'none', overflow: 'hidden', minHeight: '4rem' }} />
          </div>
        </div>
      </SidePanel>

      {/* ═══ PANNEAU DOUBLONS ════════════════════════════════════════════ */}
      <SidePanel open={dupPanel} onOpenChange={(v) => { if (!v) setDupPanel(false); }} title="Détection & Fusion de doublons" size="xl">
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-5">

            {/* Critères */}
            <div className="p-4 rounded-xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
              <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text)' }}>Critères de détection</p>
              <div className="flex items-center gap-3 flex-wrap">
                <CriteriaDropdown columns={dupColumns} selected={dupCriteria} onChange={setDupCriteria} />
                {dupCriteria.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {dupCriteria.map(k => {
                      const col = dupColumns.find(c => c.key === k);
                      return (
                        <span key={k} className="flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold"
                          style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                          {col?.label || k}
                          <button onClick={() => setDupCriteria(c => c.filter(x => x !== k))} className="ml-1 hover:opacity-70">
                            <X size={10} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <button onClick={detectDuplicates} disabled={dupDetecting || dupCriteria.length === 0}
                data-testid="detect-btn"
                className="mt-3 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                style={{ background: 'var(--primary-gradient)' }}>
                {dupDetecting ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Search size={14} />}
                Détecter les doublons
              </button>
            </div>

            {/* Résultats */}
            {dupGroups.length > 0 && (
              <div className="space-y-4">
                <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
                  {dupGroups.length} groupe{dupGroups.length > 1 ? 's' : ''} de doublons trouvé{dupGroups.length > 1 ? 's' : ''}
                </p>
                {dupGroups.map(group => (
                  <div key={group.key} className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                    <div className="px-4 py-2.5 flex items-center justify-between" style={{ background: '#FFFBEB', borderBottom: '1px solid var(--border)' }}>
                      <div className="flex items-center gap-2">
                        <AlertTriangle size={14} style={{ color: '#D97706' }} />
                        <span className="text-xs font-semibold" style={{ color: '#D97706' }}>
                          {group.count} entrées identiques
                        </span>
                        {group.count > 2 && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#FEF3C7', color: '#92400E' }}>
                            Fusionnez 2 par 2 — sélectionnez la fiche maître et un seul doublon depuis le tableau
                          </span>
                        )}
                      </div>
                      <button onClick={() => handleMergeGroup(group)}
                        disabled={merging || !dupMasters[group.key] || group.entreprises.length > 2}
                        title={group.entreprises.length > 2 ? 'Fusion limitée à 2 fiches (contrainte Odoo)' : undefined}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-40 transition-all hover:opacity-90"
                        style={{ background: (dupMasters[group.key] && group.entreprises.length <= 2) ? '#D97706' : '#9CA3AF' }}>
                        <Merge size={12} /> Fusionner
                      </button>
                    </div>
                    <table className="w-full" style={{ fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                          <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)', width: 32 }}>Maître</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Nom</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Types</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Email</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Ville</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>SIRET</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.entreprises.map(e => {
                          const isMaster = dupMasters[group.key] === e.entreprise_id;
                          return (
                            <tr key={e.entreprise_id}
                              onClick={() => setDupMasters(m => ({ ...m, [group.key]: e.entreprise_id }))}
                              style={{ borderBottom: '1px solid var(--border)', background: isMaster ? '#F0FDF4' : 'white', cursor: 'pointer', transition: 'background .15s' }}>
                              <td className="px-3 py-2.5 text-center">
                                <div className={`w-4 h-4 rounded-full border mx-auto flex items-center justify-center ${isMaster ? 'border-transparent' : ''}`}
                                  style={{ background: isMaster ? '#22C55E' : 'white', borderColor: isMaster ? '#22C55E' : 'var(--border)' }}>
                                  {isMaster && <Check size={10} color="white" strokeWidth={3} />}
                                </div>
                              </td>
                              <td className="px-3 py-2.5 font-semibold" style={{ color: 'var(--text)' }}>{e.nom}</td>
                              <td className="px-3 py-2.5">
                                <div className="flex gap-1">{(e.types_entreprise || []).map(t => <TypeBadge key={t} type={t} />)}</div>
                              </td>
                              <td className="px-3 py-2.5" style={{ color: 'var(--text-muted)' }}>{e.email || '—'}</td>
                              <td className="px-3 py-2.5" style={{ color: 'var(--text-muted)' }}>{e.ville || '—'}</td>
                              <td className="px-3 py-2.5" style={{ color: 'var(--text-muted)' }}>{e.siret || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex-none px-6 py-4 flex justify-end" style={{ borderTop: '1px solid var(--border)' }}>
            <button onClick={() => setDupPanel(false)} className="px-4 py-2 rounded-lg text-sm border transition-all"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Fermer</button>
          </div>
        </div>
      </SidePanel>

      {/* ── Modal envoi relevé de compte par mail (Annuaire) ─── */}
      {showReleveMail && releveEntrepriseRef.current && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col" style={{ maxHeight: '90vh' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-2">
                <Mail size={16} style={{ color: '#2563EB' }} />
                <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Envoyer le relevé de compte</span>
              </div>
              <button onClick={() => setShowReleveMail(false)} className="rounded-lg p-1 hover:bg-gray-100"><X size={16} /></button>
            </div>
            {releveMailLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin" style={{ color: '#7C3AED' }} /></div>
            ) : (
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Destinataires *</label>
                  {(() => {
                    const contacts = entContactsList.filter(p => p.email);
                    return (
                      <div className="space-y-2">
                        {contacts.length > 0 && (
                          <div className="border rounded-xl overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                            {contacts.map((p, i) => {
                              const checked = releveMail.destinataires.includes(p.email);
                              return (
                                <label key={p.personne_id || i} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 select-none"
                                  style={{ borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
                                  <input type="checkbox" checked={checked} onChange={e => setReleveMail(m => ({ ...m, destinataires: e.target.checked ? [...m.destinataires, p.email] : m.destinataires.filter(x => x !== p.email) }))} className="rounded" style={{ accentColor: '#2563EB' }} />
                                  <div>
                                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{[p.prenom, p.nom].filter(Boolean).join(' ') || p.email}</p>
                                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{p.email}</p>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        )}
                        <AddEmailInput onAdd={email => setReleveMail(m => m.destinataires.includes(email) ? m : { ...m, destinataires: [...m.destinataires, email] })} />
                        {releveMail.destinataires.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {releveMail.destinataires.map(email => (
                              <span key={email} className="flex items-center gap-1 px-2 py-1 rounded-full text-xs" style={{ background: '#EFF6FF', color: '#2563EB' }}>
                                {email}
                                <button onClick={() => setReleveMail(m => ({ ...m, destinataires: m.destinataires.filter(x => x !== email) }))} className="hover:opacity-70"><X size={10} /></button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sujet</label>
                  <input type="text" value={releveMail.sujet} onChange={e => setReleveMail(m => ({ ...m, sujet: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none" style={{ borderColor: 'var(--border)', color: 'var(--text)' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Corps du message</label>
                  <textarea value={releveMail.corps} onChange={e => { setReleveMail(m => ({ ...m, corps: e.target.value })); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none resize-none overflow-hidden"
                    style={{ borderColor: 'var(--border)', color: 'var(--text)', minHeight: '10rem' }} />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: 'var(--border)' }}>
              <button onClick={() => setShowReleveMail(false)} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--text-muted)' }}>Annuler</button>
              <button disabled={releveMailSending || releveMailLoading || !releveMail.destinataires.length}
                onClick={async () => {
                  if (!releveMail.destinataires.length) { toast.error('Sélectionnez au moins un destinataire'); return; }
                  setReleveMailSending(true);
                  try {
                    const resp = await api.envoyerRelevePdfMail(releveEntrepriseRef.current.entreprise_id, { destinataires: releveMail.destinataires, sujet: releveMail.sujet, corps: releveMail.corps });
                    toast.success(resp.data.message || 'Relevé envoyé');
                    setShowReleveMail(false);
                  } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi"); }
                  finally { setReleveMailSending(false); }
                }}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: '#2563EB' }}>
                {releveMailSending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                {releveMailSending ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Modal fusion personnes ─────────────────────────────────────────── */}
      {mergePerModal && (
        <div
          data-testid="merge-persons-modal"
          style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setMergePerModal(null); }}>
          <div style={{ background: 'var(--surface, white)', borderRadius: 16, padding: '24px', maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div className="flex items-center gap-2 mb-1">
              <Merge size={18} style={{ color: '#D97706' }} />
              <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Fusionner les contacts</h2>
            </div>
            <p className="text-sm mb-5" style={{ color: 'var(--text-muted)' }}>
              Choisissez la fiche maître qui sera conservée. Les champs vides du maître seront complétés depuis le doublon, les entreprises liées seront transférées.
            </p>
            <div className="space-y-2 mb-5">
              {mergePerModal.personnes.map(p => {
                const isMaster = mergePerModal.masterId === p.personne_id;
                const fullName = [p.prenom, p.nom].filter(Boolean).join(' ');
                return (
                  <div key={p.personne_id}
                    data-testid={`merge-per-option-${p.personne_id}`}
                    onClick={() => setMergePerModal(m => ({ ...m, masterId: p.personne_id }))}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer border transition-all"
                    style={{
                      borderColor: isMaster ? '#D97706' : 'var(--border)',
                      background: isMaster ? '#FFFBEB' : 'var(--bg-subtle, #f9fafb)',
                    }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
                      style={{ background: isMaster ? '#FEF3C7' : 'var(--bg-secondary)', color: isMaster ? '#92400E' : 'var(--text-muted)' }}>
                      {(p.prenom || p.nom || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{fullName || '—'}</p>
                      {p.entreprise_nom && (
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{p.entreprise_nom}</p>
                      )}
                      {p.email && (
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{p.email}</p>
                      )}
                    </div>
                    {isMaster && (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{ background: '#FEF3C7', color: '#92400E' }}>Maître</span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setMergePerModal(null)}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'white', cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={confirmMergePersonnes} disabled={mergingPer || !mergePerModal.masterId}
                data-testid="confirm-merge-persons-btn"
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, color: 'white', background: '#D97706', cursor: 'pointer', opacity: mergingPer ? 0.7 : 1 }}>
                {mergingPer ? 'Fusion en cours…' : 'Confirmer la fusion'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
